import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Modal,
  TextInput,
  Alert,
  Dimensions,
  Image,
  RefreshControl,
  ActivityIndicator,
  FlatList,
  Linking,
  Platform,
 KeyboardAvoidingView, Share, Switch, Animated,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import * as ImagePicker from 'expo-image-picker';
import { DetailsAPI, PayrollIntegrationAPI } from '../../../services/employeeApi';

const { width, height } = Dimensions.get('window');

// Default employee data structure
export const defaultEmployee = {
  id: '',
  employeeId: '',
  name: '',
  position: '',
  department: '',
  email: '',
  phone: '',
  location: '',
  avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
  currentStatus: 'onboarding',
  statusText: 'Onboarding',
  startDate: new Date().toISOString(),
  hireDate: new Date().toISOString(),
  manager: 'Sarah Johnson',
  buddy: 'Mike Chen',
  baseSalary: 65000,
  bonus: 5000,
  stockOptions: 10000,
  totalPackage: 80000,
  daysWorked: 0,
  overallScore: 75,
  performanceScore: 80,
  satisfactionScore: 85,
  teamIntegrationScore: 70,
  completedMilestones: 2,
  totalMilestones: 8,
  completedTraining: 1,
  skills: ['JavaScript', 'React', 'Node.js', 'TypeScript'],
  recentActivities: [
    {
      id: '1',
      title: 'Completed Security Training',
      description: 'Finished mandatory security awareness course',
      timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      icon: 'shield-check',
      color: '#10B981'
    },
    {
      id: '2',
      title: 'Team Meeting',
      description: 'Attended weekly team sync meeting',
      timestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      icon: 'account-group',
      color: '#3B82F6'
    },
    {
      id: '3',
      title: 'Project Kickoff',
      description: 'Joined new project team meeting',
      timestamp: new Date().toISOString(),
      icon: 'rocket-launch',
      color: '#8B5CF6'
    }
  ],
  performanceIndicators: [
    { id: '1', name: 'Productivity', value: 85, trend: 'up' },
    { id: '2', name: 'Quality', value: 90, trend: 'up' },
    { id: '3', name: 'Collaboration', value: 75, trend: 'neutral' },
    { id: '4', name: 'Initiative', value: 80, trend: 'up' }
  ],
  successIndicators: [
    'Quick learner',
    'Positive attitude',
    'Good team player',
    'Proactive communication'
  ],
  riskFactors: [
    'Needs more technical mentoring',
    'Slow to adapt to processes'
  ],
  onboardingProgress: 25,
  onboardingMilestones: [
    {
      id: '1',
      name: 'HR Paperwork',
      completed: true,
      completedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: '2',
      name: 'Equipment Setup',
      completed: true,
      completedDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: '3',
      name: 'Security Training',
      completed: false,
    },
    {
      id: '4',
      name: 'Team Introductions',
      completed: false,
    },
    {
      id: '5',
      name: 'Project Assignment',
      completed: false,
    },
    {
      id: '6',
      name: 'First Code Review',
      completed: false,
    },
    {
      id: '7',
      name: 'Probation Review',
      completed: false,
    },
    {
      id: '8',
      name: 'Full Integration',
      completed: false,
    }
  ],
  trainingModules: [
    {
      id: '1',
      name: 'Company Policies',
      completed: true,
      completedDate: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
      score: 95
    },
    {
      id: '2',
      name: 'Security Awareness',
      completed: false,
    },
    {
      id: '3',
      name: 'Development Standards',
      completed: false,
    },
    {
      id: '4',
      name: 'Product Training',
      completed: false,
    }
  ],
  equipmentSetup: {
    laptop: { model: 'MacBook Pro 16"', setupComplete: true },
    accounts: { 
      email: true, 
      system: true, 
      tools: false,
      vpn: true
    },
    access: { 
      building: true, 
      systems: true,
      repositories: false
    }
  },
  teamIntegrationDetails: {
    metMembers: 3,
    totalMembers: 8
  },
  assignedTeamMembers: [
    'Sarah Johnson',
    'Mike Chen',
    'Alex Rodriguez',
    'Priya Patel',
    'David Kim',
    'Maria Garcia',
    'James Wilson',
    'Lisa Thompson'
  ],
  metTeamMembers: [
    'Sarah Johnson',
    'Mike Chen',
    'Alex Rodriguez'
  ],
  goals: [
    {
      id: 'goal_1',
      title: 'Complete onboarding training',
      description: 'Finish all required training modules within first 30 days',
      priority: 'high',
      dueDate: new Date(Date.now() + 20 * 24 * 60 * 60 * 1000).toISOString(),
      progress: 50,
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'goal_2',
      title: 'First project contribution',
      description: 'Make meaningful contribution to team project',
      priority: 'medium',
      dueDate: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(),
      progress: 20,
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    }
  ],
  managerFeedback: [
    {
      id: 'feedback_1',
      date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      rating: 4,
      feedback: 'Great start! Showing good potential and eagerness to learn.',
      reviewer: 'Sarah Johnson',
      type: 'manager'
    }
  ],
  buddyFeedback: [
    {
      id: 'feedback_2',
      date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
      rating: 5,
      feedback: 'Very quick to adapt and ask good questions. Great addition to the team!',
      reviewer: 'Mike Chen',
      type: 'buddy'
    }
  ],
  documents: [
    {
      id: 'doc_1',
      name: 'Employment Contract',
      type: 'contract',
      uploadDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      fileUrl: '',
      size: '2.4 MB'
    },
    {
      id: 'doc_2',
      name: 'NDA Agreement',
      type: 'legal',
      uploadDate: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
      fileUrl: '',
      size: '1.8 MB'
    }
  ],
  createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  updatedAt: new Date().toISOString()
};

// Safely normalize any employee object — ensures every array field exists
const normalizeEmployee = (raw) => {
  if (!raw || typeof raw !== 'object') return { ...defaultEmployee };
  const safeArr  = (v) => Array.isArray(v) ? v : [];
  const safeStr  = (v, fb='') => (v != null && v !== '') ? String(v) : fb;
  const safeNum  = (v, fb=0) => { const n=parseFloat(v); return isNaN(n)?fb:n; };
  const safeBool = (v, fb=false) => typeof v==='boolean' ? v : fb;
  return {
    ...defaultEmployee,
    ...raw,
    // Identity
    id:                    safeStr(raw.id || raw.employeeId || raw.employee_id),
    employeeId:            safeStr(raw.employeeId || raw.employee_id || raw.id),
    name:                  safeStr(raw.name || raw.full_name || raw.employee_name, 'Unknown'),
    email:                 safeStr(raw.email || raw.contact?.email),
    phone:                 safeStr(raw.phone || raw.contact?.phone),
    position:              safeStr(raw.position || raw.job_title || raw.jobTitle || raw.role),
    jobTitle:              safeStr(raw.jobTitle || raw.job_title || raw.position),
    department:            safeStr(raw.department),
    location:              safeStr(raw.location || raw.work_location),
    status:                safeStr(raw.status, 'active'),
    employmentType:        safeStr(raw.employmentType || raw.employment_type, 'Full-time'),
    startDate:             safeStr(raw.startDate || raw.start_date || raw.hireDate),
    // Numbers
    salary:                safeNum(raw.salary || raw.base_salary, 0),
    totalPackage:          safeNum(raw.totalPackage || raw.total_package || raw.salary, 0),
    overallScore:          safeNum(raw.overallScore || raw.overall_score || raw.rating, 75),
    performanceScore:      safeNum(raw.performanceScore || raw.performance_score, 80),
    satisfactionScore:     safeNum(raw.satisfactionScore || raw.satisfaction_score, 75),
    teamIntegrationScore:  safeNum(raw.teamIntegrationScore || raw.team_integration_score, 70),
    completedMilestones:   safeNum(raw.completedMilestones || raw.completed_milestones, 0),
    totalMilestones:       safeNum(raw.totalMilestones || raw.total_milestones, 8),
    completedTraining:     safeNum(raw.completedTraining || raw.completed_training, 0),
    daysWorked:            safeNum(raw.daysWorked || raw.days_worked, 0),
    // Arrays — critical: these must never be undefined
    skills:                safeArr(raw.skills),
    recentActivities:      safeArr(raw.recentActivities || raw.recent_activities || raw.activities),
    performanceIndicators: safeArr(raw.performanceIndicators || raw.performance_indicators),
    successIndicators:     safeArr(raw.successIndicators || raw.success_indicators),
    riskFactors:           safeArr(raw.riskFactors || raw.risk_factors),
    onboardingMilestones:  safeArr(raw.onboardingMilestones || raw.onboarding_milestones),
    trainingModules:       safeArr(raw.trainingModules || raw.training_modules),
    assignedTeamMembers:   safeArr(raw.assignedTeamMembers || raw.team_members || raw.assigned_team_members),
    goals:                 safeArr(raw.goals),
    managerFeedback:       safeArr(raw.managerFeedback || raw.manager_feedback),
    buddyFeedback:         safeArr(raw.buddyFeedback || raw.buddy_feedback),
    documents:             safeArr(raw.documents),
    tags:                  safeArr(raw.tags),
    certifications:        safeArr(raw.certifications),
    languages:             safeArr(raw.languages),
    emergencyContacts:     safeArr(raw.emergencyContacts || raw.emergency_contacts),
  };
};

export default function EmployeeDetailsScreen({ navigation, route }) {
  const { employee: initialEmployee, employeeId, onEmployeeUpdate } = route?.params || {};
  
  const [employee, setEmployee] = useState(normalizeEmployee(initialEmployee || defaultEmployee));
  // ── Company logo ──────────────────────────────────────────────────────────
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(!initialEmployee);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newGoal, setNewGoal] = useState({ title: '', description: '', priority: 'medium' });
  const [newFeedback, setNewFeedback] = useState({ rating: 5, feedback: '', type: 'manager' });
  const [newDocument, setNewDocument] = useState({ name: '', type: 'contract' });
  
  // Payroll Tab State
  const [payrollData, setPayrollData] = useState({
    salary: 0,
    hourlyRate: null,
    payFrequency: 'monthly',
    payMethod: 'BACS',
    paymentDate: 28,
    taxCode: '1257L',
    niNumber: '',
    niCategory: 'A',
    studentLoan: 'none',
    pensionEnrolled: true,
    pensionScheme: 'NEST',
    employeeContribution: 5,
    employerContribution: 3,
    bankName: '',
    sortCode: '',
    accountNumber: '',
    accountName: '',
    deductions: [],
    benefits: [],
    ytdGross: 0,
    ytdTax: 0,
    ytdNI: 0,
    ytdPension: 0,
    ytdNet: 0,
    payslips: [],
  });
  const [showEditPayrollModal, setShowEditPayrollModal] = useState(false);
  const [editPayrollField, setEditPayrollField] = useState(null);
  const [editPayrollValue, setEditPayrollValue] = useState('');
  const [payrollLoading, setPayrollLoading] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [showPayslipDetail, setShowPayslipDetail] = useState(false);
  // New feature states
  const [showLeaveModal, setShowLeaveModal]       = useState(false);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [showEmailModal, setShowEmailModal]       = useState(false);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [leaveType, setLeaveType]                 = useState('annual');
  const [leaveDays, setLeaveDays]                 = useState('');
  const [leaveNote, setLeaveNote]                 = useState('');
  const [promotionTitle, setPromotionTitle]       = useState('');
  const [promotionSalary, setPromotionSalary]     = useState('');
  const [promotionNote, setPromotionNote]         = useState('');
  const [emailSubject, setEmailSubject]           = useState('');
  const [emailBody, setEmailBody]                 = useState('');
  const [newEmergencyName, setNewEmergencyName]   = useState('');
  const [newEmergencyPhone, setNewEmergencyPhone] = useState('');
  const [newEmergencyRel, setNewEmergencyRel]     = useState('');

  // Tab configuration
  const tabs = [
    { id: 'overview', name: 'Overview', icon: 'account-circle' },
    { id: 'performance', name: 'Performance', icon: 'trending-up' },
    { id: 'onboarding', name: 'Onboarding', icon: 'school' },
    { id: 'team', name: 'Team', icon: 'account-group' },
    { id: 'payroll', name: 'Payroll', icon: 'currency-gbp' },
    { id: 'goals', name: 'Goals', icon: 'target' },
    { id: 'feedback', name: 'Feedback', icon: 'comment-text' },
    { id: 'documents', name: 'Documents', icon: 'file-document' },
    { id: 'leave', name: 'Leave', icon: 'calendar-clock' },
    { id: 'attendance', name: 'Attendance', icon: 'clock-check' },
    { id: 'contact', name: 'Contact', icon: 'phone' },
  ];

  // ─── Payroll Data Functions ───────────────────────────────────────────────
  const loadPayrollData = async (emp) => {
    const empData = emp || employee;
    if (!empData) return;
    setPayrollLoading(true);
    try {
      const empId = empData.id || empData.employeeId;

      // ── Try live API first ─────────────────────────────────────────────────
      try {
        const [payrollRes, payslipsRes, ytdRes] = await Promise.all([
          DetailsAPI.getPayroll(empId),
          DetailsAPI.getPayslips(empId),
          DetailsAPI.getYtd(empId),
        ]);
        const apiPayroll  = payrollRes.data  ?? null;
        const apiPayslips = Array.isArray(payslipsRes.data) ? payslipsRes.data : [];
        const apiYtd      = ytdRes.data      ?? {};

        if (apiPayroll) {
          setPayrollData({
            salary:               apiPayroll.salary          ?? 0,
            hourlyRate:           apiPayroll.hourlyRate       ?? null,
            payFrequency:         apiPayroll.payFrequency     ?? 'monthly',
            payMethod:            apiPayroll.payMethod        ?? 'BACS',
            paymentDate:          apiPayroll.paymentDate      ?? 28,
            taxCode:              apiPayroll.taxCode          ?? '1257L',
            niNumber:             apiPayroll.niNumber         ?? '',
            niCategory:           apiPayroll.niCategory       ?? 'A',
            studentLoan:          apiPayroll.studentLoan      ?? 'none',
            pensionEnrolled:      apiPayroll.pensionEnrolled  ?? true,
            pensionScheme:        apiPayroll.pensionScheme    ?? 'NEST',
            employeeContribution: apiPayroll.employeeContribution ?? 5,
            employerContribution: apiPayroll.employerContribution ?? 3,
            bankName:             apiPayroll.bankName         ?? '',
            sortCode:             apiPayroll.sortCode         ?? '',
            accountNumber:        apiPayroll.accountNumber    ?? '',
            accountName:          apiPayroll.accountName      ?? '',
            deductions:           apiPayroll.deductions       ?? [],
            benefits:             apiPayroll.benefits         ?? [],
            ytdGross:   apiYtd.gross   ?? 0,
            ytdTax:     apiYtd.tax     ?? 0,
            ytdNI:      apiYtd.ni      ?? 0,
            ytdPension: apiYtd.pension ?? 0,
            ytdNet:     apiYtd.net     ?? 0,
            payslips:   apiPayslips.sort((a, b) =>
              new Date(b.period || b.date || 0) - new Date(a.period || a.date || 0)
            ),
          });
          setPayrollLoading(false);
          return;
        }
      } catch (apiErr) {
        console.warn('[DetailsAPI] payroll load failed, falling back to cache:', apiErr.message);
      }

      // ── AsyncStorage fallback ──────────────────────────────────────────────
      // Load from payroll sync data
      const syncRaw = await AsyncStorage.getItem('payroll_employee_sync');
      const syncData = syncRaw ? JSON.parse(syncRaw) : {};
      const mySync = syncData[empData.id] || syncData[empData.email] || null;
      
      // Load payroll employees
      const payrollEmpsRaw = await AsyncStorage.getItem('payroll_employees');
      const payrollEmps = payrollEmpsRaw ? JSON.parse(payrollEmpsRaw) : [];
      const payrollEmp = payrollEmps.find(p =>
        p.id === empData.id ||
        (p.contact?.email && p.contact.email === empData.email) ||
        p.employeeNumber === empData.employeeId
      );

      // Load payslips for this employee
      const payslipsRaw = await AsyncStorage.getItem('payroll_payslips');
      const allPayslips = payslipsRaw ? JSON.parse(payslipsRaw) : [];
      const empPayslips = allPayslips.filter(p =>
        p.employeeId === empData.id ||
        p.employeeEmail === empData.email ||
        p.employeeName === empData.name
      ).sort((a, b) => new Date(b.period || b.date || 0) - new Date(a.period || a.date || 0));

      // Calculate YTD
      const currentYear = new Date().getFullYear();
      const ytdPayslips = empPayslips.filter(p => new Date(p.period || p.date || 0).getFullYear() === currentYear);
      const ytdGross = ytdPayslips.reduce((s, p) => s + (p.gross || p.grossPay || 0), 0);
      const ytdTax = ytdPayslips.reduce((s, p) => s + (p.tax || p.incomeTax || 0), 0);
      const ytdNI = ytdPayslips.reduce((s, p) => s + (p.ni || p.nationalInsurance || 0), 0);
      const ytdPension = ytdPayslips.reduce((s, p) => s + (p.employeePension || p.pension || 0), 0);
      const ytdNet = ytdPayslips.reduce((s, p) => s + (p.net || p.netPay || 0), 0);

      setPayrollData({
        salary: payrollEmp?.payroll?.salary || mySync?.salary || empData.baseSalary || empData.salary || 0,
        hourlyRate: payrollEmp?.payroll?.hourlyRate || null,
        payFrequency: payrollEmp?.payroll?.payFrequency || mySync?.payFrequency || 'monthly',
        payMethod: payrollEmp?.payroll?.payMethod || 'BACS',
        paymentDate: payrollEmp?.payroll?.paymentDate || mySync?.paymentDate || 28,
        taxCode: payrollEmp?.taxCode || mySync?.taxCode || '1257L',
        niNumber: payrollEmp?.niNumber || empData.niNumber || '',
        niCategory: payrollEmp?.niCategory || 'A',
        studentLoan: payrollEmp?.studentLoan || 'none',
        pensionEnrolled: payrollEmp?.payroll?.pension?.enrolled ?? true,
        pensionScheme: payrollEmp?.payroll?.pension?.scheme || 'NEST',
        employeeContribution: (payrollEmp?.payroll?.pension?.employeeContribution || 0.05) * 100,
        employerContribution: (payrollEmp?.payroll?.pension?.employerContribution || 0.03) * 100,
        bankName: payrollEmp?.payroll?.bankDetails?.bankName || '',
        sortCode: payrollEmp?.payroll?.bankDetails?.sortCode || '',
        accountNumber: payrollEmp?.payroll?.bankDetails?.accountNumber ? '****' + payrollEmp.payroll.bankDetails.accountNumber.slice(-4) : '',
        accountName: payrollEmp?.payroll?.bankDetails?.accountName || empData.name || '',
        deductions: payrollEmp?.payroll?.deductions || [],
        benefits: payrollEmp?.payroll?.benefits || [],
        ytdGross, ytdTax, ytdNI, ytdPension, ytdNet,
        payslips: empPayslips.slice(0, 12),
      });
    } catch (err) {
      console.error('Error loading payroll data:', err);
    } finally {
      setPayrollLoading(false);
    }
  };

  const savePayrollData = async (updates) => {
    // ── Also write to API ──────────────────────────────────────────────────
    const empId = employee?.id || employee?.employeeId || employeeId;
    if (empId) {
      DetailsAPI.update(empId, { payroll: updates }).catch(err =>
        console.warn('[DetailsAPI] PUT payroll failed:', err.message)
      );
      // Update tax code separately if changed
      if (updates.taxCode) {
        PayrollIntegrationAPI.updateTaxCode(empId, updates.taxCode).catch(() => {});
      }
    }
    try {
      const syncRaw = await AsyncStorage.getItem('payroll_employee_sync');
      const syncData = syncRaw ? JSON.parse(syncRaw) : {};
      const key = employee?.id || employee?.email || 'unknown';
      syncData[key] = {
        ...syncData[key],
        ...updates,
        updatedAt: new Date().toISOString(),
        employeeId: employee?.id,
        employeeName: employee?.name,
        email: employee?.email,
      };
      await AsyncStorage.setItem('payroll_employee_sync', JSON.stringify(syncData));
      
      // Also update in payroll_employees if exists
      const payrollEmpsRaw = await AsyncStorage.getItem('payroll_employees');
      if (payrollEmpsRaw) {
        const payrollEmps = JSON.parse(payrollEmpsRaw);
        const idx = payrollEmps.findIndex(p =>
          p.id === employee?.id ||
          (p.contact?.email === employee?.email)
        );
        if (idx !== -1) {
          payrollEmps[idx] = {
            ...payrollEmps[idx],
            taxCode: updates.taxCode || payrollEmps[idx].taxCode,
            niNumber: updates.niNumber || payrollEmps[idx].niNumber,
            payroll: {
              ...payrollEmps[idx].payroll,
              salary: updates.salary || payrollEmps[idx].payroll?.salary,
              payFrequency: updates.payFrequency || payrollEmps[idx].payroll?.payFrequency,
              paymentDate: updates.paymentDate || payrollEmps[idx].payroll?.paymentDate,
            },
          };
          await AsyncStorage.setItem('payroll_employees', JSON.stringify(payrollEmps));
        }
      }
      
      setPayrollData(prev => ({ ...prev, ...updates }));
      Alert.alert('Success', 'Payroll details updated');
    } catch (err) {
      Alert.alert('Error', 'Failed to save payroll data');
    }
  };

  const formatPayrollCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatPayPeriodLabel = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  const loadCompanyProfile = async () => {
    try {
      loadCompanyProfile();
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l) setCompanyLogo(l);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const res = await fetch(`https://api.jobeng.ai/api/data/company_profiles?company_id=${cid}`,
        { headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp) {
        setCompanyName(cp.company_name || cp.name || '');
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l) setCompanyLogo(l);
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch {}
  };

  useEffect(() => {
    if (!initialEmployee && employeeId) {
      loadEmployeeData();
    } else if (initialEmployee) {
      loadPayrollData(initialEmployee);
    }
  }, [employeeId]);

  const loadEmployeeData = async () => {
    try {
      setLoading(true);
      const id = employeeId;

      // ── 1. Try live API ────────────────────────────────────────────────────
      let emp = null;
      try {
        const res = await DetailsAPI.get(id);
        emp = res.data ?? null;
        if (emp?.id) {
          await AsyncStorage.setItem(`detail_emp_${id}`, JSON.stringify(emp));
        }
      } catch (apiErr) {
        console.warn('[DetailsAPI] GET employee failed, trying cache:', apiErr.message);
      }

      // ── 2. AsyncStorage fallback ───────────────────────────────────────────
      if (!emp) {
        // Try per-employee cache first
        try {
          const cached = await AsyncStorage.getItem(`detail_emp_${id}`);
          if (cached) emp = JSON.parse(cached);
        } catch (_) {}
      }
      if (!emp) {
        // Try hired_employees list
        try {
          const stored = await AsyncStorage.getItem('hired_employees');
          if (stored) {
            const list = JSON.parse(stored);
            emp = list.find(e => e.id === id || e.employeeId === id) || null;
          }
        } catch (_) {}
      }

      if (emp) {
        setEmployee(normalizeEmployee({...defaultEmployee,...emp}));
        // Load payroll in parallel without blocking render
        loadPayrollData(emp).catch(console.warn);

        // Eagerly pre-fetch other tabs from API
        Promise.allSettled([
          DetailsAPI.getGoals(id),
          DetailsAPI.getWarnings(id),
          DetailsAPI.getFeedback(id),
          DetailsAPI.getDocuments(id),
          DetailsAPI.getActivity(id),
        ]);
      } else {
        Alert.alert('Employee Not Found', "Could not load this employee's record.", [
          { text: 'Go Back', onPress: () => navigation.goBack() },
        ]);
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      Alert.alert('Error', 'Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadEmployeeData();
    setRefreshing(false);
  }, []);

  const updateEmployee = async (updates) => {
    const id = employee?.id || employee?.employeeId || employeeId;
    const updatedEmployee = { ...employee, ...updates, updatedAt: new Date().toISOString() };

    // Optimistic update
    setEmployee(normalizeEmployee(updatedEmployee));

    try {
      // ── 1. Persist to API ──────────────────────────────────────────────────
      try {
        const res = await DetailsAPI.update(id, updatedEmployee);
        const saved = res.data ?? updatedEmployee;
        // Merge API response (may have server-computed fields)
        const merged = { ...updatedEmployee, ...saved };
        setEmployee(normalizeEmployee(merged));
        await AsyncStorage.setItem(`detail_emp_${id}`, JSON.stringify(merged));

        // Trigger payroll sync for salary/role changes
        if (updates.salary || updates.jobTitle || updates.department) {
          DetailsAPI.syncPayroll(id).catch(() => {});
        }

        if (onEmployeeUpdate) onEmployeeUpdate(merged);
        return merged;
      } catch (apiErr) {
        console.warn('[DetailsAPI] PUT employee failed, persisting locally:', apiErr.message);
        // API failed — still save locally
      }

      // ── 2. AsyncStorage fallback ────────────────────────────────────────────
      await AsyncStorage.setItem(`detail_emp_${id}`, JSON.stringify(updatedEmployee));
      const stored = await AsyncStorage.getItem('hired_employees');
      if (stored) {
        const list = JSON.parse(stored);
        const idx  = list.findIndex(e => e.id === id || e.employeeId === id);
        if (idx !== -1) {
          list[idx] = updatedEmployee;
          await AsyncStorage.setItem('hired_employees', JSON.stringify(list));
        }
      }
      if (onEmployeeUpdate) onEmployeeUpdate(updatedEmployee);
      return updatedEmployee;
    } catch (error) {
      console.error('Error updating employee:', error);
      // Revert optimistic update on hard failure
      setEmployee(normalizeEmployee(employee));
      Alert.alert('Error', 'Failed to update employee data');
      throw error;
    }
  };

  const handleEdit = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue?.toString() || '');
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editingField || !editValue.trim()) {
      Alert.alert('Error', 'Please enter a value');
      return;
    }
    
    try {
      const updates = { [editingField]: editValue.trim() };
      await updateEmployee(updates);
      setShowEditModal(false);
      setEditingField(null);
      setEditValue('');
      Alert.alert('Success', 'Employee updated successfully');
    } catch (error) {
      Alert.alert('Error', 'Failed to update employee');
    }
  };

  const addGoal = async () => {
    if (!newGoal.title.trim() || !newGoal.description.trim()) {
      Alert.alert('Error', 'Please fill in all goal fields');
      return;
    }
    
    try {
      const goal = {
        id: `goal_${Date.now()}`,
        ...newGoal,
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        progress: 0,
        createdAt: new Date().toISOString(),
      };
      
      const updatedGoals = [...(employee.goals || []), goal];
      await updateEmployee({ goals: updatedGoals });
      
      setNewGoal({ title: '', description: '', priority: 'medium' });
      setShowGoalModal(false);
      Alert.alert('Success', 'Goal added successfully');
    } catch (error) {
      Alert.alert('Error', 'Failed to add goal');
    }
  };

  const updateGoalProgress = async (goalId, progress) => {
    try {
      const updatedGoals = (employee.goals||[]).map(goal =>
        goal.id === goalId ? { ...goal, progress } : goal
      );
      await updateEmployee({ goals: updatedGoals });
    } catch (error) {
      Alert.alert('Error', 'Failed to update goal progress');
    }
  };

  const deleteGoal = async (goalId) => {
    Alert.alert(
      'Delete Goal',
      'Are you sure you want to delete this goal?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedGoals = (employee.goals||[]).filter(goal => goal.id !== goalId);
              await updateEmployee({ goals: updatedGoals });
              Alert.alert('Success', 'Goal deleted successfully');
            } catch (error) {
              Alert.alert('Error', 'Failed to delete goal');
            }
          }
        }
      ]
    );
  };

  const addFeedback = async () => {
    if (!newFeedback.feedback.trim()) {
      Alert.alert('Error', 'Please enter feedback text');
      return;
    }
    
    try {
      const feedback = {
        id: `feedback_${Date.now()}`,
        date: new Date().toISOString(),
        rating: newFeedback.rating,
        feedback: newFeedback.feedback.trim(),
        reviewer: 'Current User',
        type: newFeedback.type,
      };
      
      const feedbackKey = newFeedback.type === 'manager' ? 'managerFeedback' : 'buddyFeedback';
      const updatedFeedback = [...(employee[feedbackKey] || []), feedback];
      await updateEmployee({ [feedbackKey]: updatedFeedback });
      
      setNewFeedback({ rating: 5, feedback: '', type: 'manager' });
      setShowFeedbackModal(false);
      Alert.alert('Success', 'Feedback added successfully');
    } catch (error) {
      Alert.alert('Error', 'Failed to add feedback');
    }
  };

  const completeMilestone = async (milestoneId) => {
    try {
      const updatedMilestones = (employee.onboardingMilestones||[]).map(milestone =>
        milestone.id === milestoneId 
          ? { ...milestone, completed: true, completedDate: new Date().toISOString() }
          : milestone
      );
      
      const completedCount = updatedMilestones.filter(m => m.completed).length;
      const totalCount = updatedMilestones.length;
      const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
      
      await updateEmployee({ 
        onboardingMilestones: updatedMilestones,
        completedMilestones: completedCount,
        onboardingProgress: progress
      });
      
      // Update overall score based on progress
      const newOverallScore = Math.min(100, employee.overallScore + 5);
      await updateEmployee({ overallScore: newOverallScore });
      
    } catch (error) {
      Alert.alert('Error', 'Failed to complete milestone');
    }
  };

  const completeTraining = async (trainingId, score = 100) => {
    try {
      const updatedTraining = (employee.trainingModules||[]).map(module =>
        module.id === trainingId 
          ? { ...module, completed: true, completedDate: new Date().toISOString(), score }
          : module
      );
      
      const completedCount = updatedTraining.filter(m => m.completed).length;
      await updateEmployee({ 
        trainingModules: updatedTraining,
        completedTraining: completedCount
      });
      
      // Update performance score
      const newPerformanceScore = Math.min(100, employee.performanceScore + 10);
      await updateEmployee({ performanceScore: newPerformanceScore });
      
    } catch (error) {
      Alert.alert('Error', 'Failed to complete training');
    }
  };

  const markTeamMemberMet = async (memberName) => {
    try {
      const updatedMetMembers = [...new Set([...employee.metTeamMembers, memberName])];
      const metCount = updatedMetMembers.length;
      const totalCount = (employee.assignedTeamMembers||[]).length;
      const integrationScore = totalCount > 0 ? Math.round((metCount / totalCount) * 100) : 0;
      
      await updateEmployee({ 
        metTeamMembers: updatedMetMembers,
        teamIntegrationDetails: {
          ...employee.teamIntegrationDetails,
          metMembers: metCount
        },
        teamIntegrationScore: integrationScore
      });
    } catch (error) {
      Alert.alert('Error', 'Failed to update team member status');
    }
  };

  const updateEquipmentStatus = async (category, item, status) => {
    try {
      const updatedEquipment = {
        ...employee.equipmentSetup,
        [category]: {
          ...employee.equipmentSetup[category],
          [item]: status
        }
      };
      
      await updateEmployee({ equipmentSetup: updatedEquipment });
      Alert.alert('Success', 'Equipment status updated');
    } catch (error) {
      Alert.alert('Error', 'Failed to update equipment status');
    }
  };

  const addDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (result.type === 'success') {
        const document = {
          id: `doc_${Date.now()}`,
          name: newDocument.name || result.name,
          type: newDocument.type,
          uploadDate: new Date().toISOString(),
          fileUrl: result.uri,
          size: `${(result.size / (1024 * 1024)).toFixed(1)} MB`
        };

        const updatedDocuments = [...(employee.documents || []), document];
        await updateEmployee({ documents: updatedDocuments });
        
        setNewDocument({ name: '', type: 'contract' });
        setShowDocumentModal(false);
        Alert.alert('Success', 'Document uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      Alert.alert('Error', 'Failed to upload document');
    }
  };

  const deleteDocument = async (documentId) => {
    Alert.alert(
      'Delete Document',
      'Are you sure you want to delete this document?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const updatedDocuments = (employee.documents||[]).filter(doc => doc.id !== documentId);
              await updateEmployee({ documents: updatedDocuments });
              Alert.alert('Success', 'Document deleted successfully');
            } catch (error) {
              Alert.alert('Error', 'Failed to delete document');
            }
          }
        }
      ]
    );
  };

  const updateEmployeeStatus = async (newStatus, statusText) => {
    try {
      await updateEmployee({ 
        currentStatus: newStatus, 
        statusText: statusText 
      });
      Alert.alert('Success', `Employee status updated to ${statusText}`);
      setShowPerformanceModal(false);
    } catch (error) {
      Alert.alert('Error', 'Failed to update employee status');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'first_week': return '#10B981';
      case 'onboarding': return '#3B82F6';
      case 'probation': return '#F59E0B';
      case 'established': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#10B981';
    if (score >= 80) return '#3B82F6';
    if (score >= 70) return '#F59E0B';
    return '#EF4444';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const handleContact = (type, value) => {
    let url = '';
    switch (type) {
      case 'email':
        url = `mailto:${value}`;
        break;
      case 'phone':
        url = `tel:${value}`;
        break;
      case 'message':
        Alert.alert('Message', `Would you like to message ${value}?`);
        return;
    }
    
    Linking.openURL(url).catch(err => 
      Alert.alert('Error', 'Could not open contact application')
    );
  };

  const generatePerformanceReport = () => {
    Alert.alert(
      'Performance Report',
      'Performance report has been generated and saved to documents.',
      [{ text: 'OK' }]
    );
    setShowPerformanceModal(false);
  };

  const scheduleReviewMeeting = () => {
    const date = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    Alert.alert(
      'Meeting Scheduled',
      `Performance review meeting scheduled for ${formatDate(date.toISOString())}`,
      [{ text: 'OK' }]
    );
    setShowPerformanceModal(false);
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
      </TouchableOpacity>
      
      <Text style={styles.headerTitle}>Employee Details</Text>
      
      <TouchableOpacity 
        style={styles.menuButton}
        onPress={() => setShowPerformanceModal(true)}
      >
        <MaterialCommunityIcons name="dots-vertical" size={24} color="#FFFFFF" />
      </TouchableOpacity>
    </View>
  );

  // ── Photo update ──────────────────────────────────────────────────────────
  const handleUpdatePhoto = () => {
    Alert.alert(
      'Update Employee Photo',
      'Choose photo source',
      [
        {
          text: 'Take Photo',
          onPress: async () => {
            const { status } = await ImagePicker.requestCameraPermissionsAsync();
            if (status !== 'granted') { Alert.alert('Permission Required', 'Allow camera access to take photos.'); return; }
            const result = await ImagePicker.launchCameraAsync({ allowsEditing: true, aspect: [1, 1], quality: 0.5, base64: true });
            if (!result.canceled && result.assets?.[0]?.uri) applyPhoto(result.assets[0].uri);
          },
        },
        {
          text: 'Choose from Library',
          onPress: async () => {
            const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (status !== 'granted') { Alert.alert('Permission Required', 'Allow photo library access.'); return; }
            const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, allowsEditing: true, aspect: [1, 1], quality: 0.5, base64: true });
            if (!result.canceled && result.assets?.[0]?.uri) applyPhoto(result.assets[0].uri);
          },
        },
        { text: 'Cancel', style: 'cancel' },
      ]
    );
  };

  const applyPhoto = async (uri) => {
    // Update local state immediately
    setEmployee(prev => ({ ...prev, avatar: uri }));
    // Save to AsyncStorage
    try {
      const stored = await AsyncStorage.getItem(`detail_emp_${employeeId}`);
      const emp = stored ? JSON.parse(stored) : {};
      await AsyncStorage.setItem(`detail_emp_${employeeId}`, JSON.stringify({ ...emp, avatar: uri }));
      // Also update the shared employee cache
      const allRaw = await AsyncStorage.getItem('hr_employees');
      if (allRaw) {
        const all = JSON.parse(allRaw);
        const updated = all.map(e => (e.id === employeeId || e.id === String(employeeId)) ? { ...e, avatar: uri } : e);
        await AsyncStorage.setItem('hr_employees', JSON.stringify(updated));
      }
    } catch (_) {}
    // Push to API (non-blocking)
    try {
      const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      await fetch(`https://api.jobeng.ai/api/employees/${employeeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        body: JSON.stringify({ profile_image: uri }),
      });
    } catch (_) {}
  };

  const renderEmployeeCard = () => (
    <View style={styles.employeeCard}>
      <View style={styles.employeeHeader}>
        <TouchableOpacity onPress={handleUpdatePhoto} style={{ position:'relative' }}>
          <Image
            source={{ uri: employee.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(employee.name||'Employee')}&background=0e4e96&color=fff&size=200` }}
            style={styles.avatar}
          />
          <View style={{
            position:'absolute', bottom:4, right:4,
            width:32, height:32, borderRadius:16,
            backgroundColor:'#0e4e96', borderWidth:2, borderColor:'#fff',
            alignItems:'center', justifyContent:'center',
            shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.2, shadowRadius:3, elevation:3,
          }}>
            <MaterialCommunityIcons name="camera" size={16} color="#fff"/>
          </View>
        </TouchableOpacity>
        <View style={styles.employeeInfo}>
          <View style={styles.nameRow}>
            <Text style={styles.employeeName}>{employee.name}</Text>
            <View style={[styles.statusBadge, { backgroundColor: getStatusColor(employee.currentStatus) }]}>
              <Text style={styles.statusText}>{employee.statusText}</Text>
            </View>
          </View>
          <Text style={styles.employeePosition}>{employee.position}</Text>
          <Text style={styles.employeeDepartment}>{employee.department}</Text>
          <Text style={styles.employeeId}>ID: {employee.employeeId}</Text>
        </View>
      </View>
      
      <View style={styles.quickStats}>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{employee.daysWorked}</Text>
          <Text style={styles.statLabel}>Days</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={[styles.statValue, { color: getScoreColor(employee.overallScore) }]}>
            {employee.overallScore}%
          </Text>
          <Text style={styles.statLabel}>Overall</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>
            {employee.completedMilestones}/{employee.totalMilestones}
          </Text>
          <Text style={styles.statLabel}>Milestones</Text>
        </View>
        <View style={styles.statItem}>
          <Text style={styles.statValue}>{formatCurrency(employee.totalPackage)}</Text>
          <Text style={styles.statLabel}>Package</Text>
        </View>
      </View>
    </View>
  );

  const renderTabNavigation = () => (
    <View style={styles.tabContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabScrollContent}
      >
        {tabs.map((tab) => (
          <TouchableOpacity
            key={tab.id}
            style={[
              styles.tab,
              activeTab === tab.id && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.id)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.id ? '#8B5CF6' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.id && styles.activeTabText
            ]}>
              {tab.name}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderOverviewTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Contact Information</Text>
          <TouchableOpacity onPress={() => handleEdit('email', employee.email)}>
            <MaterialCommunityIcons name="pencil" size={20} color="#8B5CF6" />
          </TouchableOpacity>
        </View>
        <View style={styles.contactInfo}>
          <TouchableOpacity 
            style={styles.contactItem}
            onPress={() => handleContact('email', employee.email)}
          >
            <MaterialCommunityIcons name="email" size={20} color="#6B7280" />
            <Text style={styles.contactText}>{employee.email}</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.contactItem}
            onPress={() => handleContact('phone', employee.phone)}
          >
            <MaterialCommunityIcons name="phone" size={20} color="#6B7280" />
            <Text style={styles.contactText}>{employee.phone}</Text>
          </TouchableOpacity>
          <View style={styles.contactItem}>
            <MaterialCommunityIcons name="map-marker" size={20} color="#6B7280" />
            <Text style={styles.contactText}>{employee.location}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Employment Details</Text>
        <View style={styles.detailsGrid}>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}>Start Date</Text>
            <Text style={styles.detailValue}>{formatDate(employee.startDate)}</Text>
          </View>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}>Hire Date</Text>
            <Text style={styles.detailValue}>{formatDate(employee.hireDate)}</Text>
          </View>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}>Manager</Text>
            <Text style={styles.detailValue}>{employee.manager}</Text>
          </View>
          <View style={styles.detailItem}>
            <Text style={styles.detailLabel}>Buddy</Text>
            <Text style={styles.detailValue}>{employee.buddy}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Compensation Package</Text>
        <View style={styles.compensationGrid}>
          <View style={styles.compensationItem}>
            <Text style={styles.compensationLabel}>Base Salary</Text>
            <Text style={styles.compensationValue}>{formatCurrency(employee.baseSalary)}</Text>
          </View>
          <View style={styles.compensationItem}>
            <Text style={styles.compensationLabel}>Bonus</Text>
            <Text style={styles.compensationValue}>{formatCurrency(employee.bonus)}</Text>
          </View>
          <View style={styles.compensationItem}>
            <Text style={styles.compensationLabel}>Stock Options</Text>
            <Text style={styles.compensationValue}>{formatCurrency(employee.stockOptions)}</Text>
          </View>
          <View style={styles.compensationItem}>
            <Text style={styles.compensationLabel}>Total Package</Text>
            <Text style={[styles.compensationValue, styles.totalPackage]}>
              {formatCurrency(employee.totalPackage)}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Skills & Expertise</Text>
        <View style={styles.skillsContainer}>
          {(employee.skills||[]).map((skill, index) => (
            <View key={index} style={styles.skillTag}>
              <Text style={styles.skillText}>{skill}</Text>
            </View>
          ))}
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Recent Activity</Text>
        {(employee.recentActivities||[]).slice(0, 3).map((activity) => (
          <View key={activity.id} style={styles.activityItem}>
            <View style={[styles.activityIcon, { backgroundColor: activity.color }]}>
              <MaterialCommunityIcons name={activity.icon} size={16} color="#FFFFFF" />
            </View>
            <View style={styles.activityContent}>
              <Text style={styles.activityTitle}>{activity.title}</Text>
              <Text style={styles.activityDescription}>{activity.description}</Text>
              <Text style={styles.activityTime}>{formatTimeAgo(activity.timestamp)}</Text>
            </View>
          </View>
        ))}
      </View>
    </View>
  );

  const renderPerformanceTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Overview</Text>
        <View style={styles.performanceGrid}>
          <View style={styles.performanceCard}>
            <Text style={styles.performanceLabel}>Overall Score</Text>
            <Text style={[styles.performanceValue, { color: getScoreColor(employee.overallScore) }]}>
              {employee.overallScore}%
            </Text>
          </View>
          <View style={styles.performanceCard}>
            <Text style={styles.performanceLabel}>Performance</Text>
            <Text style={[styles.performanceValue, { color: getScoreColor(employee.performanceScore) }]}>
              {employee.performanceScore}%
            </Text>
          </View>
          <View style={styles.performanceCard}>
            <Text style={styles.performanceLabel}>Satisfaction</Text>
            <Text style={[styles.performanceValue, { color: getScoreColor(employee.satisfactionScore) }]}>
              {employee.satisfactionScore}%
            </Text>
          </View>
          <View style={styles.performanceCard}>
            <Text style={styles.performanceLabel}>Team Integration</Text>
            <Text style={[styles.performanceValue, { color: getScoreColor(employee.teamIntegrationScore) }]}>
              {employee.teamIntegrationScore}%
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Performance Indicators</Text>
        {(employee.performanceIndicators||[]).map((indicator) => (
          <View key={indicator.id} style={styles.indicatorItem}>
            <View style={styles.indicatorHeader}>
              <Text style={styles.indicatorName}>{indicator.name}</Text>
              <View style={styles.indicatorTrend}>
                <MaterialCommunityIcons 
                  name={indicator.trend === 'up' ? 'trending-up' : 'trending-neutral'} 
                  size={16} 
                  color={indicator.trend === 'up' ? '#10B981' : '#6B7280'} 
                />
                <Text style={[styles.indicatorValue, { color: getScoreColor(indicator.value) }]}>
                  {indicator.value}%
                </Text>
              </View>
            </View>
            <View style={styles.progressBar}>
              <View 
                style={[
                  styles.progressFill, 
                  { 
                    width: `${indicator.value}%`,
                    backgroundColor: getScoreColor(indicator.value)
                  }
                ]} 
              />
            </View>
          </View>
        ))}
      </View>

      {(employee.successIndicators||[]).length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Success Indicators</Text>
          <View style={styles.indicatorsContainer}>
            {(employee.successIndicators||[]).map((indicator, index) => (
              <View key={index} style={styles.successIndicator}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
                <Text style={styles.successText}>{indicator}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {(employee.riskFactors||[]).length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Areas for Attention</Text>
          <View style={styles.indicatorsContainer}>
            {(employee.riskFactors||[]).map((risk, index) => (
              <View key={index} style={styles.riskIndicator}>
                <MaterialCommunityIcons name="alert-circle" size={16} color="#EF4444" />
                <Text style={styles.riskText}>{risk}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );

  const renderOnboardingTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Onboarding Progress</Text>
        <View style={styles.progressOverview}>
          <View style={styles.progressCircle}>
            <Text style={styles.progressPercentage}>{employee.onboardingProgress}%</Text>
            <Text style={styles.progressLabel}>Complete</Text>
          </View>
          <View style={styles.progressStats}>
            <View style={styles.progressStat}>
              <Text style={styles.progressStatValue}>{employee.completedMilestones}</Text>
              <Text style={styles.progressStatLabel}>Milestones</Text>
            </View>
            <View style={styles.progressStat}>
              <Text style={styles.progressStatValue}>{employee.completedTraining}</Text>
              <Text style={styles.progressStatLabel}>Training</Text>
            </View>
            <View style={styles.progressStat}>
              <Text style={styles.progressStatValue}>{employee.daysWorked}</Text>
              <Text style={styles.progressStatLabel}>Days</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Onboarding Milestones</Text>
        {(employee.onboardingMilestones||[]).map((milestone) => (
          <TouchableOpacity
            key={milestone.id}
            style={styles.milestoneItem}
            onPress={() => !milestone.completed && completeMilestone(milestone.id)}
            disabled={milestone.completed}
          >
            <View style={[
              styles.milestoneIcon,
              { backgroundColor: milestone.completed ? '#10B981' : '#E5E7EB' }
            ]}>
              <MaterialCommunityIcons 
                name={milestone.completed ? 'check' : 'clock-outline'} 
                size={16} 
                color={milestone.completed ? '#FFFFFF' : '#6B7280'} 
              />
            </View>
            <View style={styles.milestoneContent}>
              <Text style={styles.milestoneName}>{milestone.name}</Text>
              {milestone.completed ? (
                <Text style={styles.milestoneDate}>
                  Completed {formatDate(milestone.completedDate)}
                </Text>
              ) : (
                <TouchableOpacity 
                  style={styles.completeButton}
                  onPress={() => completeMilestone(milestone.id)}
                >
                  <Text style={styles.completeButtonText}>Mark Complete</Text>
                </TouchableOpacity>
              )}
            </View>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Training Progress</Text>
        {(employee.trainingModules||[]).map((module) => (
          <TouchableOpacity
            key={module.id}
            style={styles.trainingItem}
            onPress={() => !module.completed && completeTraining(module.id)}
            disabled={module.completed}
          >
            <View style={styles.trainingHeader}>
              <Text style={styles.trainingName}>{module.name}</Text>
              {module.completed && (
                <View style={styles.trainingScore}>
                  <Text style={styles.scoreText}>{module.score}%</Text>
                </View>
              )}
            </View>
            <View style={styles.trainingStatus}>
              {module.completed ? (
                <Text style={styles.trainingCompleted}>
                  Completed {formatDate(module.completedDate)}
                </Text>
              ) : (
                <TouchableOpacity 
                  style={styles.completeButton}
                  onPress={() => completeTraining(module.id)}
                >
                  <Text style={styles.completeButtonText}>Mark Complete</Text>
                </TouchableOpacity>
              )}
            </View>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Equipment & Setup</Text>
        <View style={styles.setupGrid}>
          <View style={styles.setupCategory}>
            <Text style={styles.setupCategoryTitle}>Hardware</Text>
            <TouchableOpacity 
              style={styles.setupItem}
              onPress={() => updateEquipmentStatus('laptop', 'setupComplete', !employee.equipmentSetup.laptop.setupComplete)}
            >
              <Text style={styles.setupItemName}>Laptop: {employee.equipmentSetup.laptop.model}</Text>
              <MaterialCommunityIcons 
                name={employee.equipmentSetup.laptop.setupComplete ? 'check-circle' : 'clock-outline'} 
                size={16} 
                color={employee.equipmentSetup.laptop.setupComplete ? '#10B981' : '#F59E0B'} 
              />
            </TouchableOpacity>
          </View>
          
          <View style={styles.setupCategory}>
            <Text style={styles.setupCategoryTitle}>Accounts</Text>
            {Object.entries(employee.equipmentSetup.accounts).map(([account, status]) => (
              <TouchableOpacity
                key={account}
                style={styles.setupItem}
                onPress={() => updateEquipmentStatus('accounts', account, !status)}
              >
                <Text style={styles.setupItemName}>{account.charAt(0).toUpperCase() + account.slice(1)}</Text>
                <MaterialCommunityIcons 
                  name={status ? 'check-circle' : 'clock-outline'} 
                  size={16} 
                  color={status ? '#10B981' : '#F59E0B'} 
                />
              </TouchableOpacity>
            ))}
          </View>
          
          <View style={styles.setupCategory}>
            <Text style={styles.setupCategoryTitle}>Access</Text>
            {Object.entries(employee.equipmentSetup.access).map(([access, status]) => (
              <TouchableOpacity
                key={access}
                style={styles.setupItem}
                onPress={() => updateEquipmentStatus('access', access, !status)}
              >
                <Text style={styles.setupItemName}>{access.charAt(0).toUpperCase() + access.slice(1)}</Text>
                <MaterialCommunityIcons 
                  name={status ? 'check-circle' : 'clock-outline'} 
                  size={16} 
                  color={status ? '#10B981' : '#F59E0B'} 
                />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </View>
  );

  const renderTeamTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Team Integration</Text>
        <View style={styles.teamOverview}>
          <View style={styles.teamStat}>
            <Text style={styles.teamStatValue}>{employee.teamIntegrationDetails.metMembers}</Text>
            <Text style={styles.teamStatLabel}>Members Met</Text>
          </View>
          <View style={styles.teamStat}>
            <Text style={styles.teamStatValue}>{employee.teamIntegrationDetails.totalMembers}</Text>
            <Text style={styles.teamStatLabel}>Total Team</Text>
          </View>
          <View style={styles.teamStat}>
            <Text style={[styles.teamStatValue, { color: getScoreColor(employee.teamIntegrationScore) }]}>
              {employee.teamIntegrationScore}%
            </Text>
            <Text style={styles.teamStatLabel}>Integration</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Support Network</Text>
        <View style={styles.supportNetwork}>
          <View style={styles.supportPerson}>
            <MaterialCommunityIcons name="account-tie" size={24} color="#8B5CF6" />
            <View style={styles.supportInfo}>
              <Text style={styles.supportRole}>Manager</Text>
              <Text style={styles.supportName}>{employee.manager}</Text>
            </View>
            <TouchableOpacity 
              style={styles.contactButton}
              onPress={() => handleContact('message', employee.manager)}
            >
              <MaterialCommunityIcons name="message" size={20} color="#8B5CF6" />
            </TouchableOpacity>
          </View>
          
          <View style={styles.supportPerson}>
            <MaterialCommunityIcons name="account-heart" size={24} color="#10B981" />
            <View style={styles.supportInfo}>
              <Text style={styles.supportRole}>Buddy</Text>
              <Text style={styles.supportName}>{employee.buddy}</Text>
            </View>
            <TouchableOpacity 
              style={styles.contactButton}
              onPress={() => handleContact('message', employee.buddy)}
            >
              <MaterialCommunityIcons name="message" size={20} color="#10B981" />
            </TouchableOpacity>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Team Members</Text>
        <View style={styles.teamMembers}>
          {(employee.assignedTeamMembers||[]).map((member, index) => {
            const hasMet = employee.metTeamMembers.includes(member);
            return (
              <TouchableOpacity
                key={index}
                style={styles.teamMember}
                onPress={() => !hasMet && markTeamMemberMet(member)}
                disabled={hasMet}
              >
                <View style={[
                  styles.memberAvatar,
                  { backgroundColor: hasMet ? '#10B981' : '#E5E7EB' }
                ]}>
                  <Text style={[
                    styles.memberInitials,
                    { color: hasMet ? '#FFFFFF' : '#6B7280' }
                  ]}>
                    {member.split(' ').map(n => n[0]).join('')}
                  </Text>
                </View>
                <Text style={styles.memberName}>{member}</Text>
                {hasMet ? (
                  <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
                ) : (
                  <TouchableOpacity 
                    style={styles.meetButton}
                    onPress={() => markTeamMemberMet(member)}
                  >
                    <Text style={styles.meetButtonText}>Meet</Text>
                  </TouchableOpacity>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </View>
  );

  const renderPayrollTab = () => (
    <View style={styles.tabContent}>
      {payrollLoading ? (
        <View style={{ alignItems: 'center', padding: 40 }}>
          <ActivityIndicator size="large" color="#8B5CF6" />
          <Text style={{ color: '#6B7280', marginTop: 12 }}>Loading payroll data…</Text>
        </View>
      ) : (
        <>
          {/* Pay Summary */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Pay Summary</Text>
              <TouchableOpacity
                style={styles.addButton}
                onPress={() => navigation?.navigate && navigation.navigate('PayrollManagement')}
              >
                <MaterialCommunityIcons name="open-in-new" size={18} color="#8B5CF6" />
              </TouchableOpacity>
            </View>
            <View style={styles.compensationGrid}>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>Annual Salary</Text>
                <Text style={[styles.compensationValue, { fontSize: 18, color: '#8B5CF6' }]}>
                  {formatPayrollCurrency(payrollData.salary)}
                </Text>
              </View>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>Monthly Gross</Text>
                <Text style={styles.compensationValue}>
                  {formatPayrollCurrency(payrollData.salary / 12)}
                </Text>
              </View>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>Pay Frequency</Text>
                <Text style={styles.compensationValue}>{payrollData.payFrequency}</Text>
              </View>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>Pay Method</Text>
                <Text style={styles.compensationValue}>{payrollData.payMethod}</Text>
              </View>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>Payment Day</Text>
                <Text style={styles.compensationValue}>{payrollData.paymentDate}th of month</Text>
              </View>
            </View>
          </View>

          {/* Tax & NI */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Tax & National Insurance</Text>
            <View style={styles.detailsGrid}>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Tax Code</Text>
                <Text style={styles.detailValue}>{payrollData.taxCode}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>NI Number</Text>
                <Text style={styles.detailValue}>{payrollData.niNumber || '—'}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>NI Category</Text>
                <Text style={styles.detailValue}>{payrollData.niCategory}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Student Loan</Text>
                <Text style={styles.detailValue}>{payrollData.studentLoan === 'none' ? 'None' : String(payrollData.studentLoan ?? '').toUpperCase()}</Text>
              </View>
            </View>
          </View>

          {/* Pension */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Pension</Text>
            <View style={styles.detailsGrid}>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Auto-Enrolled</Text>
                <Text style={[styles.detailValue, { color: payrollData.pensionEnrolled ? '#10B981' : '#EF4444' }]}>
                  <Text>{payrollData.pensionEnrolled ? 'Yes' : 'No'}</Text>
                </Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Scheme</Text>
                <Text style={styles.detailValue}>{payrollData.pensionScheme}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Employee Contribution</Text>
                <Text style={styles.detailValue}>{payrollData.employeeContribution}%</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Employer Contribution</Text>
                <Text style={styles.detailValue}>{payrollData.employerContribution}%</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Monthly Emp. Pension</Text>
                <Text style={styles.detailValue}>
                  {formatPayrollCurrency((payrollData.salary / 12) * (payrollData.employeeContribution / 100))}
                </Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Monthly Emp'r Pension</Text>
                <Text style={styles.detailValue}>
                  {formatPayrollCurrency((payrollData.salary / 12) * (payrollData.employerContribution / 100))}
                </Text>
              </View>
            </View>
          </View>

          {/* Bank Details */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Bank Details</Text>
            <View style={styles.detailsGrid}>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Account Name</Text>
                <Text style={styles.detailValue}>{payrollData.accountName || '—'}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Bank Name</Text>
                <Text style={styles.detailValue}>{payrollData.bankName || '—'}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Sort Code</Text>
                <Text style={styles.detailValue}>{payrollData.sortCode || '—'}</Text>
              </View>
              <View style={styles.detailItem}>
                <Text style={styles.detailLabel}>Account Number</Text>
                <Text style={styles.detailValue}>{payrollData.accountNumber || '—'}</Text>
              </View>
            </View>
          </View>

          {/* YTD Figures */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Year-to-Date {new Date().getFullYear()}</Text>
            <View style={styles.compensationGrid}>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>YTD Gross</Text>
                <Text style={styles.compensationValue}>{payrollData.ytdGross > 0 ? formatPayrollCurrency(payrollData.ytdGross) : '—'}</Text>
              </View>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>YTD Income Tax</Text>
                <Text style={[styles.compensationValue, { color: '#EF4444' }]}>{payrollData.ytdTax > 0 ? formatPayrollCurrency(payrollData.ytdTax) : '—'}</Text>
              </View>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>YTD NI</Text>
                <Text style={[styles.compensationValue, { color: '#EF4444' }]}>{payrollData.ytdNI > 0 ? formatPayrollCurrency(payrollData.ytdNI) : '—'}</Text>
              </View>
              <View style={styles.compensationItem}>
                <Text style={styles.compensationLabel}>YTD Pension</Text>
                <Text style={[styles.compensationValue, { color: '#8B5CF6' }]}>{payrollData.ytdPension > 0 ? formatPayrollCurrency(payrollData.ytdPension) : '—'}</Text>
              </View>
              <View style={[styles.compensationItem, { borderTopWidth: 1, borderTopColor: '#E5E7EB', paddingTop: 12 }]}>
                <Text style={[styles.compensationLabel, { fontWeight: '700' }]}>YTD Net Pay</Text>
                <Text style={[styles.compensationValue, styles.totalPackage, { fontSize: 18 }]}>
                  {payrollData.ytdNet > 0 ? formatPayrollCurrency(payrollData.ytdNet) : '—'}
                </Text>
              </View>
            </View>
          </View>

          {/* Deductions */}
          {(payrollData.deductions||[]).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Recurring Deductions</Text>
              {(payrollData.deductions||[]).map((ded, i) => (
                <View key={i} style={styles.detailItem}>
                  <Text style={styles.detailLabel}>{ded.name}</Text>
                  <Text style={[styles.detailValue, { color: '#EF4444' }]}>
                    -{formatPayrollCurrency(ded.amount)}/{ded.frequency}
                  </Text>
                </View>
              ))}
            </View>
          )}

          {/* Benefits */}
          {(payrollData.benefits||[]).length > 0 && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Benefits in Kind</Text>
              {(payrollData.benefits||[]).map((ben, i) => (
                <View key={i} style={styles.detailItem}>
                  <Text style={styles.detailLabel}>{ben.name}</Text>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={styles.detailValue}>{formatPayrollCurrency(ben.value)}/mo</Text>
                    {ben.taxable && <Text style={{ fontSize: 11, color: '#F59E0B' }}>Taxable</Text>}
                  </View>
                </View>
              ))}
            </View>
          )}

          {/* Payslip History */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionTitle}>Payslip History</Text>
            </View>
            {(payrollData.payslips||[]).length === 0 ? (
              <View style={styles.emptyState}>
                <MaterialCommunityIcons name="file-document-outline" size={40} color="#E5E7EB" />
                <Text style={styles.emptyStateText}>No payslips yet</Text>
              </View>
            ) : (
              (payrollData.payslips||[]).map((payslip, i) => (
                <TouchableOpacity
                  key={i}
                  style={[styles.activityItem, { paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }]}
                  onPress={() => { setSelectedPayslip(payslip); setShowPayslipDetail(true); }}
                >
                  <View style={[styles.activityIcon, { backgroundColor: '#8B5CF6' }]}>
                    <MaterialCommunityIcons name="file-document" size={16} color="#FFFFFF" />
                  </View>
                  <View style={styles.activityContent}>
                    <Text style={styles.activityTitle}>{formatPayPeriodLabel(payslip.period || payslip.date)}</Text>
                    <Text style={styles.activityDescription}>
                      Gross: {formatPayrollCurrency(payslip.gross || payslip.grossPay || 0)}
                    </Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={{ fontSize: 15, fontWeight: '700', color: '#10B981' }}>
                      {formatPayrollCurrency(payslip.net || payslip.netPay || 0)}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#9CA3AF' }}>Net</Text>
                  </View>
                </TouchableOpacity>
              ))
            )}
          </View>

          {/* Edit Payroll Button */}
          <TouchableOpacity
            style={[styles.completeButton, { width: '100%', alignItems: 'center', paddingVertical: 14, borderRadius: 12, marginBottom: 20 }]}
            onPress={() => setShowEditPayrollModal(true)}
          >
            <Text style={[styles.completeButtonText, { fontSize: 15 }]}>Edit Payroll Details</Text>
          </TouchableOpacity>
        </>
      )}
    </View>
  );

  const renderGoalsTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Goals & Objectives</Text>
        <TouchableOpacity 
          style={styles.addButton}
          onPress={() => setShowGoalModal(true)}
        >
          <MaterialCommunityIcons name="plus" size={20} color="#8B5CF6" />
        </TouchableOpacity>
      </View>

      {(employee.goals||[]).length === 0 ? (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="target" size={48} color="#E5E7EB" />
          <Text style={styles.emptyStateText}>No goals set yet</Text>
          <Text style={styles.emptyStateSubtext}>Add a goal to get started</Text>
        </View>
      ) : (
        (employee.goals||[]).map((goal) => (
          <View key={goal.id} style={styles.goalItem}>
            <View style={styles.goalHeader}>
              <Text style={styles.goalTitle}>{goal.title}</Text>
              <View style={styles.goalActions}>
                <TouchableOpacity 
                  style={styles.actionButton}
                  onPress={() => deleteGoal(goal.id)}
                >
                  <MaterialCommunityIcons name="delete" size={16} color="#EF4444" />
                </TouchableOpacity>
                <View style={[
                  styles.priorityBadge,
                  { backgroundColor: goal.priority === 'high' ? '#EF4444' : goal.priority === 'medium' ? '#F59E0B' : '#10B981' }
                ]}>
                  <Text style={styles.priorityText}>{goal.priority}</Text>
                </View>
              </View>
            </View>
            <Text style={styles.goalDescription}>{goal.description}</Text>
            <View style={styles.goalProgress}>
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { 
                      width: `${goal.progress}%`,
                      backgroundColor: getScoreColor(goal.progress)
                    }
                  ]} 
                />
              </View>
              <Text style={styles.progressText}>{goal.progress}%</Text>
            </View>
            <View style={styles.progressControls}>
              <Text style={styles.progressLabel}>Update Progress:</Text>
              <View style={styles.progressButtons}>
                {[0, 25, 50, 75, 100].map((value) => (
                  <TouchableOpacity
                    key={value}
                    style={[
                      styles.progressButton,
                      goal.progress === value && styles.progressButtonActive
                    ]}
                    onPress={() => updateGoalProgress(goal.id, value)}
                  >
                    <Text style={[
                      styles.progressButtonText,
                      goal.progress === value && styles.progressButtonTextActive
                    ]}>
                      {value}%
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <Text style={styles.goalDueDate}>Due: {formatDate(goal.dueDate)}</Text>
          </View>
        ))
      )}
    </View>
  );

  const renderFeedbackTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Feedback & Reviews</Text>
        <TouchableOpacity 
          style={styles.addButton}
          onPress={() => setShowFeedbackModal(true)}
        >
          <MaterialCommunityIcons name="plus" size={20} color="#8B5CF6" />
        </TouchableOpacity>
      </View>

      <View style={styles.section}>
        <Text style={styles.subsectionTitle}>Manager Feedback</Text>
        {(employee.managerFeedback||[]).length === 0 ? (
          <Text style={styles.noDataText}>No manager feedback yet</Text>
        ) : (
          (employee.managerFeedback||[]).map((feedback, index) => (
            <View key={index} style={styles.feedbackItem}>
              <View style={styles.feedbackHeader}>
                <Text style={styles.feedbackReviewer}>{feedback.reviewer}</Text>
                <View style={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <MaterialCommunityIcons
                      key={star}
                      name={star <= feedback.rating ? 'star' : 'star-outline'}
                      size={16}
                      color="#F59E0B"
                    />
                  ))}
                </View>
              </View>
              <Text style={styles.feedbackText}>{feedback.feedback}</Text>
              <Text style={styles.feedbackDate}>{formatDate(feedback.date)}</Text>
            </View>
          ))
        )}
      </View>

      <View style={styles.section}>
        <Text style={styles.subsectionTitle}>Buddy Feedback</Text>
        {(employee.buddyFeedback||[]).length === 0 ? (
          <Text style={styles.noDataText}>No buddy feedback yet</Text>
        ) : (
          (employee.buddyFeedback||[]).map((feedback, index) => (
            <View key={index} style={styles.feedbackItem}>
              <View style={styles.feedbackHeader}>
                <Text style={styles.feedbackReviewer}>{feedback.reviewer}</Text>
                <View style={styles.ratingContainer}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <MaterialCommunityIcons
                      key={star}
                      name={star <= feedback.rating ? 'star' : 'star-outline'}
                      size={16}
                      color="#F59E0B"
                    />
                  ))}
                </View>
              </View>
              <Text style={styles.feedbackText}>{feedback.feedback}</Text>
              <Text style={styles.feedbackDate}>{formatDate(feedback.date)}</Text>
            </View>
          ))
        )}
      </View>
    </View>
  );

  const renderDocumentsTab = () => (
    <View style={styles.tabContent}>
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Employee Documents</Text>
        <TouchableOpacity 
          style={styles.addButton}
          onPress={() => setShowDocumentModal(true)}
        >
          <MaterialCommunityIcons name="plus" size={20} color="#8B5CF6" />
        </TouchableOpacity>
      </View>

      <View style={styles.section}>
        <View style={styles.documentCategories}>
          <TouchableOpacity style={styles.documentCategory}>
            <MaterialCommunityIcons name="file-document" size={24} color="#8B5CF6" />
            <Text style={styles.documentCategoryText}>Contract</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.documentCategory}>
            <MaterialCommunityIcons name="school" size={24} color="#10B981" />
            <Text style={styles.documentCategoryText}>Training</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.documentCategory}>
            <MaterialCommunityIcons name="certificate" size={24} color="#F59E0B" />
            <Text style={styles.documentCategoryText}>Certificates</Text>
          </TouchableOpacity>
          
          <TouchableOpacity style={styles.documentCategory}>
            <MaterialCommunityIcons name="account-card-details" size={24} color="#EF4444" />
            <Text style={styles.documentCategoryText}>ID Documents</Text>
          </TouchableOpacity>
        </View>

        <Text style={styles.subsectionTitle}>Recent Documents</Text>
        {(employee.documents||[]).length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="file-document-outline" size={48} color="#E5E7EB" />
            <Text style={styles.emptyStateText}>No documents uploaded</Text>
            <Text style={styles.emptyStateSubtext}>Add a document to get started</Text>
          </View>
        ) : (
          <View style={styles.documentsList}>
            {(employee.documents||[]).map((document) => (
              <View key={document.id} style={styles.documentItem}>
                <MaterialCommunityIcons name="file-pdf-box" size={24} color="#EF4444" />
                <View style={styles.documentInfo}>
                  <Text style={styles.documentName}>{document.name}</Text>
                  <Text style={styles.documentDate}>
                    Uploaded {formatDate(document.uploadDate)} • {document.size}
                  </Text>
                </View>
                <View style={styles.documentActions}>
                  <TouchableOpacity style={styles.documentAction}>
                    <MaterialCommunityIcons name="download" size={20} color="#8B5CF6" />
                  </TouchableOpacity>
                  <TouchableOpacity 
                    style={styles.documentAction}
                    onPress={() => deleteDocument(document.id)}
                  >
                    <MaterialCommunityIcons name="delete" size={20} color="#EF4444" />
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    </View>
  );


  // ─── Leave Tab ────────────────────────────────────────────────────────────
  const renderLeaveTab = () => {
    const leaveBalance = employee.leaveBalance || {
      annual: { total: 25, used: employee.leaveUsed || 0, pending: 2 },
      sick:   { total: 10, used: employee.sickLeave || 0, pending: 0 },
      unpaid: { total: 0,  used: 0, pending: 0 },
      maternity: { total: 0, used: 0, pending: 0 },
    };
    const leaveHistory = (employee.leaveHistory || []).concat(
      employee.leaves || employee.leave_history || []
    );
    return (
      <View style={styles.tabContent}>
        {/* Balance cards */}
        <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:16 }}>
          {[
            { type:'Annual',    data:leaveBalance.annual,    color:'#3B82F6', icon:'beach' },
            { type:'Sick',      data:leaveBalance.sick,      color:'#EF4444', icon:'hospital-box' },
            { type:'Pending',   data:{ total: (leaveBalance.annual?.pending||0)+(leaveBalance.sick?.pending||0), used:0 }, color:'#F59E0B', icon:'clock-alert' },
          ].map(({ type, data, color, icon }) => {
            const remaining = (data.total||0) - (data.used||0);
            const pct = data.total > 0 ? Math.round((data.used / data.total) * 100) : 0;
            return (
              <View key={type} style={{ width: (width - 48) / 2, backgroundColor:'#fff', borderRadius:14, padding:14,
                borderTopWidth:3, borderTopColor:color, shadowColor:'#000', shadowOffset:{width:0,height:1},
                shadowOpacity:0.06, shadowRadius:4, elevation:2 }}>
                <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:8 }}>
                  <MaterialCommunityIcons name={icon} size={18} color={color} />
                  <Text style={{ fontSize:13, fontWeight:'700', color:'#374151' }}>{type}</Text>
                </View>
                <Text style={{ fontSize:24, fontWeight:'900', color }}>{type==='Pending' ? data.total : remaining}</Text>
                <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>
                  {type==='Pending' ? 'days pending approval' : `of ${data.total} days remaining`}
                </Text>
                {type!=='Pending' && data.total > 0 && (
                  <View style={{ height:4, backgroundColor:'#F1F5F9', borderRadius:2, overflow:'hidden', marginTop:8 }}>
                    <View style={{ width:`${pct}%`, height:'100%', backgroundColor:color, borderRadius:2 }}/>
                  </View>
                )}
              </View>
            );
          })}
        </View>

        {/* Apply leave button */}
        <TouchableOpacity style={{ backgroundColor:'#3B82F6', borderRadius:12, padding:14,
          flexDirection:'row', justifyContent:'center', gap:8, alignItems:'center', marginBottom:16 }}
          onPress={() => setShowLeaveModal(true)}>
          <MaterialCommunityIcons name="calendar-plus" size={18} color="#fff" />
          <Text style={{ color:'#fff', fontWeight:'800', fontSize:14 }}>Request Leave</Text>
        </TouchableOpacity>

        {/* Leave history */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Leave History</Text>
          {leaveHistory.length === 0 ? (
            <View style={{ alignItems:'center', paddingVertical:32 }}>
              <MaterialCommunityIcons name="calendar-blank" size={40} color="#E5E7EB" />
              <Text style={{ color:'#9CA3AF', marginTop:8 }}>No leave history</Text>
            </View>
          ) : leaveHistory.map((leave, i) => (
            <View key={leave.id || i} style={{ flexDirection:'row', alignItems:'center', paddingVertical:10,
              borderBottomWidth:1, borderBottomColor:'#F8FAFC' }}>
              <View style={{ width:38, height:38, borderRadius:19,
                backgroundColor: leave.status==='approved' ? '#ECFDF5' : leave.status==='rejected' ? '#FEF2F2' : '#FFFBEB',
                alignItems:'center', justifyContent:'center', marginRight:12 }}>
                <MaterialCommunityIcons
                  name={leave.status==='approved' ? 'check-circle' : leave.status==='rejected' ? 'close-circle' : 'clock'}
                  size={20} color={leave.status==='approved' ? '#10B981' : leave.status==='rejected' ? '#EF4444' : '#F59E0B'} />
              </View>
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:13, fontWeight:'700', color:'#111827' }}>
                  {leave.type || 'Annual'} Leave
                </Text>
                <Text style={{ fontSize:11, color:'#6B7280', marginTop:1 }}>
                  {leave.startDate || leave.start_date || '—'} → {leave.endDate || leave.end_date || '—'}
                  {' '}· {leave.days || leave.duration || 1} day{(leave.days||1)>1?'s':''}
                </Text>
                {leave.note && <Text style={{ fontSize:11, color:'#9CA3AF' }} numberOfLines={1}>{leave.note}</Text>}
              </View>
              <View style={{ backgroundColor: leave.status==='approved'?'#ECFDF5':leave.status==='rejected'?'#FEF2F2':'#FFFBEB',
                borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                <Text style={{ fontSize:10, fontWeight:'700',
                  color: leave.status==='approved'?'#059669':leave.status==='rejected'?'#EF4444':'#D97706' }}>
                  {(leave.status||'pending').charAt(0).toUpperCase()+(leave.status||'pending').slice(1)}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>
    );
  };

  // ─── Attendance Tab ────────────────────────────────────────────────────────
  const renderAttendanceTab = () => {
    const attendance = employee.attendance || employee.attendanceSummary || {};
    const monthly    = employee.monthlyAttendance || [];
    const daysPresent  = attendance.daysPresent  || attendance.days_present  || employee.daysWorked || 0;
    const daysAbsent   = attendance.daysAbsent   || attendance.days_absent   || 0;
    const lateArrivals = attendance.lateArrivals || attendance.late_arrivals || 0;
    const overtime     = attendance.overtime      || attendance.overtime_hours || 0;
    const workDays     = attendance.totalWorkDays || attendance.total_work_days || 22;
    const pctPresent   = workDays > 0 ? Math.round((daysPresent / workDays) * 100) : 0;
    return (
      <View style={styles.tabContent}>
        {/* Key stats */}
        <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:16 }}>
          {[
            { label:'Present',      val:daysPresent,  color:'#10B981', icon:'check-circle' },
            { label:'Absent',       val:daysAbsent,   color:'#EF4444', icon:'close-circle' },
            { label:'Late',         val:lateArrivals, color:'#F59E0B', icon:'clock-alert' },
            { label:'Overtime hrs', val:overtime,     color:'#8B5CF6', icon:'clock-plus' },
          ].map(m => (
            <View key={m.label} style={{ width:(width-48)/2, backgroundColor:'#fff', borderRadius:12, padding:14,
              borderLeftWidth:3, borderLeftColor:m.color, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:3, elevation:2 }}>
              <MaterialCommunityIcons name={m.icon} size={20} color={m.color} />
              <Text style={{ fontSize:22, fontWeight:'900', color:m.color, marginTop:6 }}>{m.val}</Text>
              <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>{m.label}</Text>
            </View>
          ))}
        </View>

        {/* Attendance rate */}
        <View style={[styles.section, { marginBottom:16 }]}>
          <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:8 }}>
            <Text style={styles.sectionTitle}>Attendance Rate</Text>
            <Text style={{ fontSize:16, fontWeight:'900', color: pctPresent>=90?'#10B981':pctPresent>=75?'#F59E0B':'#EF4444' }}>{pctPresent}%</Text>
          </View>
          <View style={{ height:10, backgroundColor:'#F1F5F9', borderRadius:5, overflow:'hidden' }}>
            <View style={{ width:`${pctPresent}%`, height:'100%', borderRadius:5,
              backgroundColor: pctPresent>=90?'#10B981':pctPresent>=75?'#F59E0B':'#EF4444' }}/>
          </View>
          <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:6 }}>
            {daysPresent} of {workDays} working days this month
          </Text>
        </View>

        {/* Monthly breakdown */}
        {monthly.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Monthly Record</Text>
            {monthly.slice(0,10).map((day, i) => (
              <View key={i} style={{ flexDirection:'row', alignItems:'center', paddingVertical:8,
                borderBottomWidth:1, borderBottomColor:'#F8FAFC' }}>
                <Text style={{ width:90, fontSize:12, color:'#6B7280' }}>{day.date || day.day || '—'}</Text>
                <Text style={{ flex:1, fontSize:12, color:'#374151' }}>
                  {day.checkIn || day.check_in || '—'} → {day.checkOut || day.check_out || '—'}
                </Text>
                <View style={{ backgroundColor:day.status==='present'?'#ECFDF5':day.status==='absent'?'#FEF2F2':'#FFFBEB',
                  borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                  <Text style={{ fontSize:10, fontWeight:'700',
                    color:day.status==='present'?'#059669':day.status==='absent'?'#EF4444':'#D97706' }}>
                    {(day.status||'—').charAt(0).toUpperCase()+(day.status||'').slice(1)}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  // ─── Contact Tab ───────────────────────────────────────────────────────────
  const renderContactTab = () => {
    const emergencyContacts = (employee.emergencyContacts||[]).concat(employee.emergency_contacts||[]);
    return (
      <View style={styles.tabContent}>
        {/* Quick contact */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Quick Contact</Text>
          <View style={{ flexDirection:'row', gap:10, marginTop:12 }}>
            {[
              { label:'Call',    icon:'phone',         color:'#10B981', onPress:()=>employee.phone&&Linking.openURL(`tel:${employee.phone}`) },
              { label:'Email',   icon:'email',         color:'#3B82F6', onPress:()=>setShowEmailModal(true) },
              { label:'Message', icon:'message-text',  color:'#8B5CF6', onPress:()=>employee.phone&&Linking.openURL(`sms:${employee.phone}`) },
              { label:'Video',   icon:'video',         color:'#0EA5E9', onPress:()=>navigation?.navigate('BusinessVideoConferenceScreen',{participantName:employee.name}) },
            ].map(btn => (
              <TouchableOpacity key={btn.label} style={{ flex:1, backgroundColor:btn.color+'15', borderRadius:12, padding:12,
                alignItems:'center', borderWidth:1, borderColor:btn.color+'30' }} onPress={btn.onPress}>
                <MaterialCommunityIcons name={btn.icon} size={22} color={btn.color} />
                <Text style={{ fontSize:11, color:btn.color, fontWeight:'700', marginTop:5 }}>{btn.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Personal details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Personal Details</Text>
          {[
            { label:'Email',      value:employee.email,       icon:'email-outline',    url:`mailto:${employee.email}` },
            { label:'Phone',      value:employee.phone,       icon:'phone-outline',    url:`tel:${employee.phone}` },
            { label:'Location',   value:employee.location||employee.address, icon:'map-marker-outline' },
            { label:'Start Date', value:employee.startDate||employee.start_date, icon:'calendar-outline' },
            { label:'Manager',    value:employee.manager||employee.reporting_to, icon:'account-supervisor-outline' },
            { label:'Emp ID',     value:employee.employeeId,  icon:'badge-account-outline' },
            { label:'LinkedIn',   value:employee.linkedin,    icon:'linkedin', url:employee.linkedin },
          ].filter(r => r.value).map((r, i) => (
            <TouchableOpacity key={i} style={{ flexDirection:'row', alignItems:'center', paddingVertical:10,
              borderBottomWidth:1, borderBottomColor:'#F8FAFC' }} onPress={()=>r.url&&Linking.openURL(r.url)} disabled={!r.url}>
              <MaterialCommunityIcons name={r.icon} size={18} color="#3B82F6" style={{ width:26 }} />
              <Text style={{ width:90, fontSize:12, color:'#9CA3AF', fontWeight:'600' }}>{r.label}</Text>
              <Text style={{ flex:1, fontSize:13, color:'#374151' }}>{r.value}</Text>
              {r.url && <MaterialCommunityIcons name="open-in-new" size={13} color="#D1D5DB" />}
            </TouchableOpacity>
          ))}
        </View>

        {/* Emergency contacts */}
        <View style={styles.section}>
          <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <Text style={styles.sectionTitle}>Emergency Contacts</Text>
            <TouchableOpacity onPress={()=>setShowEmergencyModal(true)}>
              <Text style={{ fontSize:13, color:'#3B82F6', fontWeight:'700' }}>+ Add</Text>
            </TouchableOpacity>
          </View>
          {emergencyContacts.length === 0 ? (
            <View style={{ alignItems:'center', paddingVertical:24 }}>
              <MaterialCommunityIcons name="account-alert-outline" size={40} color="#E5E7EB" />
              <Text style={{ color:'#9CA3AF', marginTop:8 }}>No emergency contacts added</Text>
            </View>
          ) : emergencyContacts.map((ec, i) => (
            <View key={ec.id||i} style={{ flexDirection:'row', alignItems:'center', paddingVertical:10,
              borderBottomWidth:1, borderBottomColor:'#F8FAFC' }}>
              <View style={{ width:40, height:40, borderRadius:20, backgroundColor:'#EFF6FF', alignItems:'center', justifyContent:'center', marginRight:12 }}>
                <MaterialCommunityIcons name="account-heart" size={20} color="#3B82F6" />
              </View>
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:13, fontWeight:'700', color:'#111827' }}>{ec.name||'—'}</Text>
                <Text style={{ fontSize:11, color:'#9CA3AF' }}>{ec.relationship||'—'} · {ec.phone||'—'}</Text>
              </View>
              {ec.phone && (
                <TouchableOpacity onPress={()=>Linking.openURL(`tel:${ec.phone}`)}
                  style={{ backgroundColor:'#ECFDF5', borderRadius:8, padding:8 }}>
                  <MaterialCommunityIcons name="phone" size={16} color="#10B981" />
                </TouchableOpacity>
              )}
            </View>
          ))}
        </View>

        {/* Promotion / Salary Review */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>HR Actions</Text>
          <View style={{ gap:10, marginTop:10 }}>
            <TouchableOpacity style={{ backgroundColor:'#8B5CF6', borderRadius:12, padding:14,
              flexDirection:'row', alignItems:'center', gap:12 }} onPress={()=>setShowPromotionModal(true)}>
              <MaterialCommunityIcons name="trending-up" size={22} color="#fff" />
              <View style={{ flex:1 }}>
                <Text style={{ color:'#fff', fontWeight:'900', fontSize:14 }}>Promotion / Salary Review</Text>
                <Text style={{ color:'rgba(255,255,255,0.8)', fontSize:11 }}>Update title or compensation</Text>
              </View>
              <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={{ backgroundColor:'#F59E0B', borderRadius:12, padding:14,
              flexDirection:'row', alignItems:'center', gap:12 }}
              onPress={()=>updateEmployeeStatus(employee.status==='active'?'on_leave':'active', 'Status updated')}>
              <MaterialCommunityIcons name="account-switch" size={22} color="#fff" />
              <View style={{ flex:1 }}>
                <Text style={{ color:'#fff', fontWeight:'900', fontSize:14 }}>Change Status</Text>
                <Text style={{ color:'rgba(255,255,255,0.8)', fontSize:11 }}>
                  Currently: {employee.status||'active'}
                </Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity style={{ backgroundColor:'#EF4444', borderRadius:12, padding:14,
              flexDirection:'row', alignItems:'center', gap:12 }}
              onPress={()=>Alert.alert('Terminate Employee', `Are you sure you want to terminate ${employee.name}?`,
                [{text:'Cancel',style:'cancel'},{text:'Terminate',style:'destructive',onPress:()=>updateEmployeeStatus('terminated','Employee terminated')}])}>
              <MaterialCommunityIcons name="account-remove" size={22} color="#fff" />
              <View style={{ flex:1 }}>
                <Text style={{ color:'#fff', fontWeight:'900', fontSize:14 }}>Terminate Employee</Text>
                <Text style={{ color:'rgba(255,255,255,0.8)', fontSize:11 }}>Remove from active roster</Text>
              </View>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverviewTab();
      case 'performance': return renderPerformanceTab();
      case 'onboarding': return renderOnboardingTab();
      case 'team': return renderTeamTab();
      case 'payroll': return renderPayrollTab();
      case 'goals': return renderGoalsTab();
      case 'feedback': return renderFeedbackTab();
      case 'documents':   return renderDocumentsTab();
      case 'leave':       return renderLeaveTab();
      case 'attendance':  return renderAttendanceTab();
      case 'contact':     return renderContactTab();
      default: return renderOverviewTab();
    }
  };

  const renderPerformanceModal = () => (
    <Modal
      visible={showPerformanceModal}
      transparent={true}
      animationType="slide"
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>Performance Actions</Text>
          
          <TouchableOpacity 
            style={styles.modalAction}
            onPress={generatePerformanceReport}
          >
            <MaterialCommunityIcons name="chart-line" size={20} color="#8B5CF6" />
            <Text style={styles.modalActionText}>Generate Performance Report</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.modalAction}
            onPress={scheduleReviewMeeting}
          >
            <MaterialCommunityIcons name="calendar" size={20} color="#10B981" />
            <Text style={styles.modalActionText}>Schedule Review Meeting</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.modalAction}
            onPress={() => handleContact('email', employee.email)}
          >
            <MaterialCommunityIcons name="email" size={20} color="#F59E0B" />
            <Text style={styles.modalActionText}>Send Progress Update</Text>
          </TouchableOpacity>

          <Text style={styles.modalSectionTitle}>Change Status</Text>
          
          <TouchableOpacity 
            style={styles.modalAction}
            onPress={() => updateEmployeeStatus('first_week', 'First Week')}
          >
            <MaterialCommunityIcons name="calendar-start" size={20} color="#10B981" />
            <Text style={styles.modalActionText}>First Week</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.modalAction}
            onPress={() => updateEmployeeStatus('onboarding', 'Onboarding')}
          >
            <MaterialCommunityIcons name="school" size={20} color="#3B82F6" />
            <Text style={styles.modalActionText}>Onboarding</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.modalAction}
            onPress={() => updateEmployeeStatus('probation', 'Probation')}
          >
            <MaterialCommunityIcons name="clock" size={20} color="#F59E0B" />
            <Text style={styles.modalActionText}>Probation</Text>
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.modalAction}
            onPress={() => updateEmployeeStatus('established', 'Established')}
          >
            <MaterialCommunityIcons name="check-circle" size={20} color="#8B5CF6" />
            <Text style={styles.modalActionText}>Established</Text>
          </TouchableOpacity>

          <View style={styles.modalButtons}>
            <TouchableOpacity 
              style={styles.modalCancelButton}
              onPress={() => setShowPerformanceModal(false)}
            >
              <Text style={styles.modalCancelText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderDocumentModal = () => (
    <Modal
      visible={showDocumentModal}
      transparent={true}
      animationType="slide"
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <Text style={styles.modalTitle}>Upload Document</Text>
          
          <TextInput
            style={styles.modalInput}
            value={newDocument.name}
            onChangeText={(text) => setNewDocument(prev => ({ ...prev, name: text }))}
            placeholder="Document name"
          />
          
          <Text style={styles.modalLabel}>Document Type</Text>
          <View style={styles.typeSelector}>
            {['contract', 'training', 'certificate', 'legal', 'other'].map((type) => (
              <TouchableOpacity
                key={type}
                style={[
                  styles.typeOption,
                  newDocument.type === type && styles.selectedType
                ]}
                onPress={() => setNewDocument(prev => ({ ...prev, type }))}
              >
                <Text style={[
                  styles.typeOptionText,
                  newDocument.type === type && styles.selectedTypeText
                ]}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.modalButtons}>
            <TouchableOpacity 
              style={styles.modalCancelButton}
              onPress={() => setShowDocumentModal(false)}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.modalSaveButton}
              onPress={addDocument}
            >
              <Text style={styles.modalSaveText}>Upload Document</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#8B5CF6" />
        <LinearGradient colors={['#8B5CF6', '#7C3AED']} style={styles.header}>
          {renderHeader()}
        </LinearGradient>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#8B5CF6" />
          <Text style={styles.loadingText}>Loading employee details...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!employee) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#8B5CF6" />
        <LinearGradient colors={['#8B5CF6', '#7C3AED']} style={styles.header}>
          {renderHeader()}
        </LinearGradient>
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="account-alert" size={64} color="#EF4444" />
          <Text style={styles.errorText}>Employee not found</Text>
          <TouchableOpacity 
            style={styles.retryButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.retryButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#8B5CF6" />
      
      <LinearGradient colors={['#8B5CF6', '#7C3AED']} style={styles.header}>
        {renderHeader()}
      </LinearGradient>

      <ScrollView 
        style={styles.content}
       refreshControl={
  <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
}
      >
        {renderEmployeeCard()}
        {renderTabNavigation()}
        {renderTabContent()}
      </ScrollView>

      {/* Edit Modal */}
      <Modal visible={showEditModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Edit {editingField}</Text>
            <TextInput
              style={styles.modalInput}
              value={editValue}
              onChangeText={setEditValue}
              placeholder={`Enter ${editingField}`}
              multiline={editingField === 'notes'}
            />
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={styles.modalCancelButton}
                onPress={() => setShowEditModal(false)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.modalSaveButton}
                onPress={saveEdit}
              >
                <Text style={styles.modalSaveText}>Save</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Goal Modal */}
      <Modal visible={showGoalModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Add New Goal</Text>
            <TextInput
              style={styles.modalInput}
              value={newGoal.title}
              onChangeText={(text) => setNewGoal(prev => ({ ...prev, title: text }))}
              placeholder="Goal title"
            />
            <TextInput
              style={[styles.modalInput, styles.modalTextArea]}
              value={newGoal.description}
              onChangeText={(text) => setNewGoal(prev => ({ ...prev, description: text }))}
              placeholder="Goal description"
              multiline
            />
            <Text style={styles.modalLabel}>Priority</Text>
            <View style={styles.prioritySelector}>
              {['low', 'medium', 'high'].map((priority) => (
                <TouchableOpacity
                  key={priority}
                  style={[
                    styles.priorityOption,
                    newGoal.priority === priority && styles.selectedPriority
                  ]}
                  onPress={() => setNewGoal(prev => ({ ...prev, priority }))}
                >
                  <Text style={[
                    styles.priorityOptionText,
                    newGoal.priority === priority && styles.selectedPriorityText
                  ]}>
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={styles.modalCancelButton}
                onPress={() => setShowGoalModal(false)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.modalSaveButton}
                onPress={addGoal}
              >
                <Text style={styles.modalSaveText}>Add Goal</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Feedback Modal */}
      <Modal visible={showFeedbackModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Add Feedback</Text>
            
            <View style={styles.feedbackTypeSelector}>
              {['manager', 'buddy'].map((type) => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.typeOption,
                    newFeedback.type === type && styles.selectedType
                  ]}
                  onPress={() => setNewFeedback(prev => ({ ...prev, type }))}
                >
                  <Text style={[
                    styles.typeOptionText,
                    newFeedback.type === type && styles.selectedTypeText
                  ]}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            <View style={styles.ratingSelector}>
              <Text style={styles.ratingLabel}>Rating:</Text>
              <View style={styles.starSelector}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <TouchableOpacity
                    key={star}
                    onPress={() => setNewFeedback(prev => ({ ...prev, rating: star }))}
                  >
                    <MaterialCommunityIcons
                      name={star <= newFeedback.rating ? 'star' : 'star-outline'}
                      size={24}
                      color="#F59E0B"
                    />
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            <TextInput
              style={[styles.modalInput, styles.modalTextArea]}
              value={newFeedback.feedback}
              onChangeText={(text) => setNewFeedback(prev => ({ ...prev, feedback: text }))}
              placeholder="Enter feedback"
              multiline
            />
            
            <View style={styles.modalButtons}>
              <TouchableOpacity 
                style={styles.modalCancelButton}
                onPress={() => setShowFeedbackModal(false)}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={styles.modalSaveButton}
                onPress={addFeedback}
              >
                <Text style={styles.modalSaveText}>Add Feedback</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Payroll Modal */}
      <Modal visible={showEditPayrollModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <ScrollView>
            <View style={[styles.modalContent, { marginVertical: 40 }]}>
              <Text style={styles.modalTitle}>Edit Payroll Details</Text>
              
              <Text style={styles.modalLabel}>Annual Salary (£)</Text>
              <TextInput
                style={styles.modalInput}
                value={String(payrollData.salary)}
                onChangeText={v => setPayrollData(p => ({ ...p, salary: parseFloat(v) || 0 }))}
                keyboardType="numeric"
                placeholder="Annual salary"
              />

              <Text style={styles.modalLabel}>Tax Code</Text>
              <TextInput
                style={styles.modalInput}
                value={payrollData.taxCode}
                onChangeText={v => setPayrollData(p => ({ ...p, taxCode: v }))}
                placeholder="e.g. 1257L"
                autoCapitalize="characters"
              />

              <Text style={styles.modalLabel}>NI Number</Text>
              <TextInput
                style={styles.modalInput}
                value={payrollData.niNumber}
                onChangeText={v => setPayrollData(p => ({ ...p, niNumber: v }))}
                placeholder="e.g. AB123456C"
                autoCapitalize="characters"
              />

              <Text style={styles.modalLabel}>NI Category</Text>
              <View style={styles.typeSelector}>
                {['A', 'B', 'C', 'D', 'H', 'J', 'M', 'Z'].map(cat => (
                  <TouchableOpacity
                    key={cat}
                    style={[styles.typeOption, payrollData.niCategory === cat && styles.selectedType]}
                    onPress={() => setPayrollData(p => ({ ...p, niCategory: cat }))}
                  >
                    <Text style={[styles.typeOptionText, payrollData.niCategory === cat && styles.selectedTypeText]}>{cat}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.modalLabel}>Pay Frequency</Text>
              <View style={styles.typeSelector}>
                {['weekly', 'bi-weekly', 'monthly'].map(freq => (
                  <TouchableOpacity
                    key={freq}
                    style={[styles.typeOption, payrollData.payFrequency === freq && styles.selectedType]}
                    onPress={() => setPayrollData(p => ({ ...p, payFrequency: freq }))}
                  >
                    <Text style={[styles.typeOptionText, payrollData.payFrequency === freq && styles.selectedTypeText]}>
                      {freq.charAt(0).toUpperCase() + freq.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.modalLabel}>Student Loan Plan</Text>
              <View style={styles.typeSelector}>
                {['none', 'plan1', 'plan2', 'plan4', 'postgrad'].map(plan => (
                  <TouchableOpacity
                    key={plan}
                    style={[styles.typeOption, payrollData.studentLoan === plan && styles.selectedType]}
                    onPress={() => setPayrollData(p => ({ ...p, studentLoan: plan }))}
                  >
                    <Text style={[styles.typeOptionText, payrollData.studentLoan === plan && styles.selectedTypeText]}>
                      {plan === 'none' ? 'None' : plan.charAt(0).toUpperCase() + plan.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.modalLabel}>Employee Pension Contribution (%)</Text>
              <TextInput
                style={styles.modalInput}
                value={String(payrollData.employeeContribution)}
                onChangeText={v => setPayrollData(p => ({ ...p, employeeContribution: parseFloat(v) || 0 }))}
                keyboardType="numeric"
              />

              <Text style={styles.modalLabel}>Employer Pension Contribution (%)</Text>
              <TextInput
                style={styles.modalInput}
                value={String(payrollData.employerContribution)}
                onChangeText={v => setPayrollData(p => ({ ...p, employerContribution: parseFloat(v) || 0 }))}
                keyboardType="numeric"
              />

              <View style={styles.modalButtons}>
                <TouchableOpacity style={styles.modalCancelButton} onPress={() => setShowEditPayrollModal(false)}>
                  <Text style={styles.modalCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.modalSaveButton}
                  onPress={async () => {
                    await savePayrollData({
                      salary: payrollData.salary,
                      taxCode: payrollData.taxCode,
                      niNumber: payrollData.niNumber,
                      niCategory: payrollData.niCategory,
                      payFrequency: payrollData.payFrequency,
                      studentLoan: payrollData.studentLoan,
                      employeeContribution: payrollData.employeeContribution / 100,
                      employerContribution: payrollData.employerContribution / 100,
                    });
                    setShowEditPayrollModal(false);
                  }}
                >
                  <Text style={styles.modalSaveText}>Save Changes</Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </View>
      </Modal>

      {/* Payslip Detail Modal */}
      <Modal visible={showPayslipDetail} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 16 }}>
              <Text style={styles.modalTitle}>Payslip</Text>
              <TouchableOpacity onPress={() => setShowPayslipDetail(false)}>
                <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
              </TouchableOpacity>
            </View>
            {selectedPayslip && (
              <ScrollView>
                <Text style={{ textAlign: 'center', fontSize: 18, fontWeight: '700', marginBottom: 16, color: '#1F2937' }}>
                  {formatPayPeriodLabel(selectedPayslip.period || selectedPayslip.date)}
                </Text>
                {[
                  { label: 'Employee', value: selectedPayslip.employeeName || employee?.name, color: '#1F2937' },
                  { label: 'Gross Pay', value: formatPayrollCurrency(selectedPayslip.gross || selectedPayslip.grossPay || 0), color: '#1F2937' },
                  { label: 'Income Tax', value: '-' + formatPayrollCurrency(selectedPayslip.tax || selectedPayslip.incomeTax || 0), color: '#EF4444' },
                  { label: 'National Insurance', value: '-' + formatPayrollCurrency(selectedPayslip.ni || selectedPayslip.nationalInsurance || 0), color: '#EF4444' },
                  { label: 'Pension', value: '-' + formatPayrollCurrency(selectedPayslip.employeePension || selectedPayslip.pension || 0), color: '#8B5CF6' },
                  { label: 'Net Pay', value: formatPayrollCurrency(selectedPayslip.net || selectedPayslip.netPay || 0), color: '#10B981', bold: true },
                ].map((row, i) => (
                  <View key={i} style={[styles.detailItem, row.bold && { backgroundColor: '#F0FDF4', borderRadius: 8, padding: 8 }]}>
                    <Text style={[styles.detailLabel, row.bold && { fontWeight: '700' }]}>{row.label}</Text>
                    <Text style={[styles.detailValue, { color: row.color }, row.bold && { fontSize: 18 }]}>{row.value}</Text>
                  </View>
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Performance Actions Modal */}
      {renderPerformanceModal()}

      {/* Document Upload Modal */}
      {renderDocumentModal()}

      {/* Leave Request Modal */}
      <Modal visible={showLeaveModal} transparent animationType="slide">
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'}
          style={{flex:1,justifyContent:'flex-end',backgroundColor:'rgba(0,0,0,0.4)'}}>
          <View style={{backgroundColor:'#fff',borderTopLeftRadius:24,borderTopRightRadius:24,padding:22,paddingBottom:Platform.OS==='ios'?40:24}}>
            <Text style={{fontSize:16,fontWeight:'800',color:'#111827',marginBottom:16}}>Request Leave</Text>
            <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',marginBottom:8}}>LEAVE TYPE</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:8,marginBottom:16}}>
              {['Annual','Sick','Unpaid','Maternity','Paternity','Compassionate'].map(t=>(
                <TouchableOpacity key={t} onPress={()=>setLeaveType(t.toLowerCase())}
                  style={{paddingHorizontal:14,paddingVertical:8,borderRadius:12,
                    backgroundColor:leaveType===t.toLowerCase()?'#3B82F6':'#F3F4F6',
                    borderWidth:1,borderColor:leaveType===t.toLowerCase()?'#3B82F6':'#E5E7EB'}}>
                  <Text style={{fontSize:12,color:leaveType===t.toLowerCase()?'#fff':'#6B7280',fontWeight:'600'}}>{t}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',marginBottom:6}}>NUMBER OF DAYS</Text>
            <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
              paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:12}}
              value={leaveDays} onChangeText={setLeaveDays} keyboardType="number-pad" placeholder="e.g. 3"/>
            <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',marginBottom:6}}>NOTE (OPTIONAL)</Text>
            <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
              paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:16,minHeight:70,textAlignVertical:'top'}}
              value={leaveNote} onChangeText={setLeaveNote} placeholder="Reason for leave…" multiline/>
            <View style={{flexDirection:'row',gap:10}}>
              <TouchableOpacity style={{flex:1,backgroundColor:'#F1F5F9',borderRadius:12,padding:13,alignItems:'center'}}
                onPress={()=>setShowLeaveModal(false)}>
                <Text style={{color:'#6B7280',fontWeight:'700'}}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{flex:1,backgroundColor:'#3B82F6',borderRadius:12,padding:13,
                alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={async()=>{
                  const leave={id:`lv_${Date.now()}`,type:leaveType,days:parseInt(leaveDays)||1,
                    note:leaveNote,status:'pending',requestedAt:new Date().toISOString()};
                  await updateEmployee({leaveHistory:[...(employee.leaveHistory||[]),leave]});
                  setShowLeaveModal(false);setLeaveDays('');setLeaveNote('');
                  Alert.alert('✅ Submitted','Leave request submitted for approval');
                }}>
                <MaterialCommunityIcons name="calendar-check" size={16} color="#fff"/>
                <Text style={{color:'#fff',fontWeight:'700'}}>Submit Request</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Promotion Modal */}
      <Modal visible={showPromotionModal} transparent animationType="slide">
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'}
          style={{flex:1,justifyContent:'flex-end',backgroundColor:'rgba(0,0,0,0.4)'}}>
          <View style={{backgroundColor:'#fff',borderTopLeftRadius:24,borderTopRightRadius:24,padding:22,paddingBottom:Platform.OS==='ios'?40:24}}>
            <Text style={{fontSize:16,fontWeight:'800',color:'#111827',marginBottom:16}}>Promotion / Salary Review</Text>
            <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',marginBottom:6}}>NEW JOB TITLE</Text>
            <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
              paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:12}}
              value={promotionTitle} onChangeText={setPromotionTitle} placeholder={employee.jobTitle||employee.position||'New title…'}/>
            <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',marginBottom:6}}>NEW SALARY</Text>
            <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
              paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:12}}
              value={promotionSalary} onChangeText={setPromotionSalary} keyboardType="numeric"
              placeholder={String(employee.salary||0)}/>
            <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',marginBottom:6}}>NOTE</Text>
            <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
              paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:16,minHeight:60,textAlignVertical:'top'}}
              value={promotionNote} onChangeText={setPromotionNote} placeholder="Reason for promotion…" multiline/>
            <View style={{flexDirection:'row',gap:10}}>
              <TouchableOpacity style={{flex:1,backgroundColor:'#F1F5F9',borderRadius:12,padding:13,alignItems:'center'}}
                onPress={()=>setShowPromotionModal(false)}>
                <Text style={{color:'#6B7280',fontWeight:'700'}}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{flex:1,backgroundColor:'#8B5CF6',borderRadius:12,padding:13,
                alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={async()=>{
                  const updates={};
                  if(promotionTitle.trim())updates.jobTitle=promotionTitle.trim();
                  if(promotionSalary)updates.salary=parseFloat(promotionSalary)||employee.salary;
                  if(Object.keys(updates).length){
                    const rec={id:`promo_${Date.now()}`,date:new Date().toISOString(),...updates,note:promotionNote};
                    updates.promotionHistory=[...(employee.promotionHistory||[]),rec];
                    await updateEmployee(updates);
                  }
                  setShowPromotionModal(false);setPromotionTitle('');setPromotionSalary('');setPromotionNote('');
                  Alert.alert('✅ Updated','Employee record updated');
                }}>
                <MaterialCommunityIcons name="trending-up" size={16} color="#fff"/>
                <Text style={{color:'#fff',fontWeight:'700'}}>Apply Changes</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Email Modal */}
      <Modal visible={showEmailModal} transparent animationType="slide">
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'}
          style={{flex:1,justifyContent:'flex-end',backgroundColor:'rgba(0,0,0,0.4)'}}>
          <View style={{backgroundColor:'#fff',borderTopLeftRadius:24,borderTopRightRadius:24,padding:22,paddingBottom:Platform.OS==='ios'?40:24}}>
            <View style={{flexDirection:'row',justifyContent:'space-between',marginBottom:14}}>
              <Text style={{fontSize:16,fontWeight:'800',color:'#111827'}}>Email {employee.name?.split(' ')[0]}</Text>
              <TouchableOpacity onPress={()=>setShowEmailModal(false)}>
                <MaterialCommunityIcons name="close" size={22} color="#6B7280"/>
              </TouchableOpacity>
            </View>
            <Text style={{fontSize:12,color:'#9CA3AF',marginBottom:8}}>To: {employee.email}</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{gap:6,marginBottom:10}}>
              {[
                {label:'Check-in',  body:`Hi ${employee.name?.split(' ')[0]},

Just checking in on your progress. How are things going?

Best regards`},
                {label:'Meeting',   body:`Hi ${employee.name?.split(' ')[0]},

I'd like to schedule a 1:1 meeting with you. Are you available this week?

Best regards`},
                {label:'Feedback',  body:`Hi ${employee.name?.split(' ')[0]},

I wanted to share some feedback on your recent work...

Best regards`},
                {label:'Congrats',  body:`Hi ${employee.name?.split(' ')[0]},

I wanted to congratulate you on your recent achievement. Great work!

Best regards`},
              ].map(t=>(
                <TouchableOpacity key={t.label} style={{backgroundColor:'#F1F5F9',borderRadius:10,paddingHorizontal:12,paddingVertical:6}}
                  onPress={()=>{setEmailSubject(t.label);setEmailBody(t.body);}}>
                  <Text style={{fontSize:11,color:'#374151',fontWeight:'600'}}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
              paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:10}}
              value={emailSubject} onChangeText={setEmailSubject} placeholder="Subject"/>
            <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
              paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:14,minHeight:100,textAlignVertical:'top'}}
              value={emailBody} onChangeText={setEmailBody} placeholder="Message…" multiline/>
            <View style={{flexDirection:'row',gap:10}}>
              <TouchableOpacity style={{flex:1,backgroundColor:'#F1F5F9',borderRadius:12,padding:13,alignItems:'center'}}
                onPress={()=>setShowEmailModal(false)}>
                <Text style={{color:'#6B7280',fontWeight:'700'}}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{flex:1,backgroundColor:'#3B82F6',borderRadius:12,padding:13,
                alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={()=>{
                  if(employee.email)Linking.openURL(`mailto:${employee.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`);
                  setShowEmailModal(false);
                }}>
                <MaterialCommunityIcons name="send" size={16} color="#fff"/>
                <Text style={{color:'#fff',fontWeight:'700'}}>Send Email</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {/* Emergency Contact Modal */}
      <Modal visible={showEmergencyModal} transparent animationType="slide">
        <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'}
          style={{flex:1,justifyContent:'flex-end',backgroundColor:'rgba(0,0,0,0.4)'}}>
          <View style={{backgroundColor:'#fff',borderTopLeftRadius:24,borderTopRightRadius:24,padding:22,paddingBottom:Platform.OS==='ios'?40:24}}>
            <Text style={{fontSize:16,fontWeight:'800',color:'#111827',marginBottom:16}}>Add Emergency Contact</Text>
            {[['Full Name','newEmergencyName',setNewEmergencyName,newEmergencyName,'e.g. Jane Smith'],
              ['Relationship','newEmergencyRel',setNewEmergencyRel,newEmergencyRel,'e.g. Spouse'],
              ['Phone Number','newEmergencyPhone',setNewEmergencyPhone,newEmergencyPhone,'+44 7xxx xxxxxx']
            ].map(([label,,setter,value,ph])=>(
              <View key={label}>
                <Text style={{fontSize:11,fontWeight:'800',color:'#9CA3AF',marginBottom:6}}>{label.toUpperCase()}</Text>
                <TextInput style={{backgroundColor:'#F9FAFB',borderWidth:1.5,borderColor:'#E5E7EB',borderRadius:12,
                  paddingHorizontal:14,paddingVertical:11,fontSize:14,color:'#111827',marginBottom:12}}
                  value={value} onChangeText={setter} placeholder={ph}/>
              </View>
            ))}
            <View style={{flexDirection:'row',gap:10}}>
              <TouchableOpacity style={{flex:1,backgroundColor:'#F1F5F9',borderRadius:12,padding:13,alignItems:'center'}}
                onPress={()=>setShowEmergencyModal(false)}>
                <Text style={{color:'#6B7280',fontWeight:'700'}}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{flex:1,backgroundColor:'#3B82F6',borderRadius:12,padding:13,
                alignItems:'center',flexDirection:'row',justifyContent:'center',gap:6}}
                onPress={async()=>{
                  if(!newEmergencyName.trim()||!newEmergencyPhone.trim()){Alert.alert('Required','Enter name and phone.');return;}
                  const ec={id:`ec_${Date.now()}`,name:newEmergencyName.trim(),relationship:newEmergencyRel.trim(),phone:newEmergencyPhone.trim()};
                  await updateEmployee({emergencyContacts:[...(employee.emergencyContacts||[]),ec]});
                  setShowEmergencyModal(false);setNewEmergencyName('');setNewEmergencyPhone('');setNewEmergencyRel('');
                }}>
                <MaterialCommunityIcons name="account-plus" size={16} color="#fff"/>
                <Text style={{color:'#fff',fontWeight:'700'}}>Add Contact</Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>

    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: 20,
  },
  menuButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    color: '#EF4444',
    marginTop: 16,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  employeeCard: {
    backgroundColor: '#FFFFFF',
    margin: 20,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  employeeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    marginRight: 16,
  },
  employeeInfo: {
    flex: 1,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  employeeName: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1F2937',
    flex: 1,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 8,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  employeePosition: {
    fontSize: 16,
    fontWeight: '600',
    color: '#8B5CF6',
    marginBottom: 2,
  },
  employeeDepartment: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 2,
  },
  employeeId: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  quickStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
  },
  statLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  tabContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabScrollContent: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#F3F4F6',
  },
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#8B5CF6',
    fontWeight: '600',
  },
  tabContent: {
    padding: 20,
  },
  section: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
  },
  subsectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 12,
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactInfo: {
    gap: 12,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  contactText: {
    fontSize: 14,
    color: '#374151',
    marginLeft: 12,
  },
  detailsGrid: {
    gap: 16,
  },
  detailItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  detailLabel: {
    fontSize: 14,
    color: '#6B7280',
    flex: 1,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    flex: 2,
    textAlign: 'right',
  },
  compensationGrid: {
    gap: 12,
  },
  compensationItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  compensationLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  compensationValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  totalPackage: {
    fontSize: 16,
    color: '#8B5CF6',
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  skillTag: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  skillText: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  activityContent: {
    flex: 1,
  },
  activityTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 2,
  },
  activityDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  activityTime: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  performanceGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  performanceCard: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#F8FAFC',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  performanceLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  performanceValue: {
    fontSize: 20,
    fontWeight: '700',
  },
  indicatorItem: {
    marginBottom: 16,
  },
  indicatorHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  indicatorName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    flex: 1,
  },
  indicatorTrend: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  indicatorValue: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 4,
  },
  progressBar: {
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  indicatorsContainer: {
    gap: 8,
  },
  successIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0FDF4',
    padding: 8,
    borderRadius: 6,
  },
  successText: {
    fontSize: 12,
    color: '#166534',
    marginLeft: 6,
  },
  riskIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    padding: 8,
    borderRadius: 6,
  },
  riskText: {
    fontSize: 12,
    color: '#991B1B',
    marginLeft: 6,
  },
  progressOverview: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  progressCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 20,
  },
  progressPercentage: {
    fontSize: 18,
    fontWeight: '700',
    color: '#8B5CF6',
  },
  progressLabel: {
    fontSize: 10,
    color: '#6B7280',
  },
  progressStats: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  progressStat: {
    alignItems: 'center',
  },
  progressStatValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1F2937',
  },
  progressStatLabel: {
    fontSize: 11,
    color: '#6B7280',
  },
  milestoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  milestoneIcon: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  milestoneContent: {
    flex: 1,
  },
  milestoneName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  milestoneDate: {
    fontSize: 12,
    color: '#10B981',
  },
  completeButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginTop: 4,
    alignSelf: 'flex-start',
  },
  completeButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  trainingItem: {
    marginBottom: 12,
  },
  trainingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  trainingName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    flex: 1,
  },
  trainingScore: {
    backgroundColor: '#F0FDF4',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  scoreText: {
    fontSize: 12,
    color: '#166534',
    fontWeight: '600',
  },
  trainingStatus: {
    marginTop: 4,
  },
  trainingCompleted: {
    fontSize: 12,
    color: '#10B981',
  },
  setupGrid: {
    gap: 16,
  },
  setupCategory: {
    backgroundColor: '#F8FAFC',
    padding: 12,
    borderRadius: 8,
  },
  setupCategoryTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  setupItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  setupItemName: {
    fontSize: 12,
    color: '#6B7280',
  },
  teamOverview: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
  },
  teamStat: {
    alignItems: 'center',
  },
  teamStatValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
  },
  teamStatLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 4,
  },
  supportNetwork: {
    gap: 16,
  },
  supportPerson: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    padding: 12,
    borderRadius: 8,
  },
  supportInfo: {
    flex: 1,
    marginLeft: 12,
  },
  supportRole: {
    fontSize: 12,
    color: '#6B7280',
  },
  supportName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  contactButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  teamMembers: {
    gap: 12,
  },
  teamMember: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    padding: 12,
    borderRadius: 8,
  },
  memberAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  memberInitials: {
    fontSize: 12,
    fontWeight: '600',
  },
  memberName: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
  },
  meetButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  meetButtonText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  goalItem: {
    backgroundColor: '#F8FAFC',
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  goalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  goalTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    flex: 1,
  },
  goalActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    padding: 4,
    marginRight: 8,
  },
  priorityBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  priorityText: {
    fontSize: 10,
    color: '#FFFFFF',
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  goalDescription: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 12,
  },
  goalProgress: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressText: {
    fontSize: 12,
    color: '#374151',
    marginLeft: 8,
    fontWeight: '600',
  },
  progressControls: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  progressLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  progressButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  progressButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: '#F3F4F6',
  },
  progressButtonActive: {
    backgroundColor: '#8B5CF6',
  },
  progressButtonText: {
    fontSize: 10,
    color: '#6B7280',
    fontWeight: '600',
  },
  progressButtonTextActive: {
    color: '#FFFFFF',
  },
  goalDueDate: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  feedbackItem: {
    backgroundColor: '#F8FAFC',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  feedbackHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  feedbackReviewer: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  ratingContainer: {
    flexDirection: 'row',
  },
  feedbackText: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  feedbackDate: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  documentCategories: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 20,
  },
  documentCategory: {
    flex: 1,
    minWidth: '45%',
    backgroundColor: '#F8FAFC',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  documentCategoryText: {
    fontSize: 12,
    color: '#374151',
    marginTop: 8,
    textAlign: 'center',
  },
  documentsList: {
    gap: 12,
  },
  documentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    padding: 12,
    borderRadius: 8,
  },
  documentInfo: {
    flex: 1,
    marginLeft: 12,
  },
  documentName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  documentDate: {
    fontSize: 12,
    color: '#6B7280',
  },
  documentActions: {
    flexDirection: 'row',
    gap: 8,
  },
  documentAction: {
    padding: 4,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  emptyStateText: {
    fontSize: 16,
    color: '#6B7280',
    marginTop: 12,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#9CA3AF',
    marginTop: 4,
  },
  noDataText: {
    textAlign: 'center',
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginVertical: 20,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: width * 0.9,
    maxHeight: height * 0.8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1F2937',
    marginBottom: 16,
    textAlign: 'center',
  },
  modalSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    marginBottom: 16,
  },
  modalTextArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  modalLabel: {
    fontSize: 14,
    color: '#374151',
    marginBottom: 8,
    fontWeight: '600',
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '600',
  },
  modalSaveButton: {
    flex: 1,
    backgroundColor: '#8B5CF6',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalSaveText: {
    fontSize: 14,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  prioritySelector: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  priorityOption: {
    flex: 1,
    backgroundColor: '#F3F4F6',
    padding: 8,
    borderRadius: 6,
    alignItems: 'center',
  },
  selectedPriority: {
    backgroundColor: '#8B5CF6',
  },
  priorityOptionText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '600',
  },
  selectedPriorityText: {
    color: '#FFFFFF',
  },
  feedbackTypeSelector: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 16,
  },
  typeSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  typeOption: {
    backgroundColor: '#F3F4F6',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    alignItems: 'center',
    minWidth: 80,
  },
  selectedType: {
    backgroundColor: '#8B5CF6',
  },
  typeOptionText: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '600',
  },
  selectedTypeText: {
    color: '#FFFFFF',
  },
  ratingSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  ratingLabel: {
    fontSize: 14,
    color: '#374151',
    marginRight: 12,
  },
  starSelector: {
    flexDirection: 'row',
    gap: 4,
  },
  modalAction: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  modalActionText: {
    fontSize: 16,
    color: '#374151',
    marginLeft: 12,
    flex: 1,
  },
});